import React, { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactFlow, { addEdge, Background, Controls, MiniMap, applyNodeChanges, applyEdgeChanges } from 'reactflow';
import 'reactflow/dist/style.css';
import CustomNode from './components/CustomNode';
import ToolCallNode from './components/ToolCallNode'; // Import the ToolCallNode component
import { nodeTypesConfig } from './nodesConfig';
import { createClient } from '@supabase/supabase-js';
import toast, { Toaster } from 'react-hot-toast';
import './App.css';
import _ from 'lodash';
import NodeSettings from './components/NodeSettings';
import FineTuningExamples from './components/FineTuningExamples';


const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const substituteVariables = (text, variables) => {
  if (!text || typeof text !== 'string') return text;
  
  let result = text;
  // Replace all occurrences of {{variableName}} with the stored value
  Object.keys(variables).forEach(varName => {
    const regex = new RegExp(`{{${varName}}}`, 'g');
    result = result.replace(regex, variables[varName] || '');
  });
  
  return result;
};

const useForceSyncNodeInputs = (nodes, nodeInputs, setNodeInputs, isSaving = false, isTesting = false) => {
  const previousActionRef = useRef({ isSaving: false, isTesting: false });
  
  useEffect(() => {
    // Detect when a save or test operation is about to happen
    if ((isSaving || isTesting) && 
        (!previousActionRef.current.isSaving && !previousActionRef.current.isTesting)) {
      
      console.log('Forcing sync of all node inputs before save/test operation');
      
      // Get the latest nodeInputs from node data
      const syncedInputs = { ...nodeInputs };
      
      nodes.forEach(node => {
        if (node.data && node.data.nodeInputs) {
          syncedInputs[node.id] = { ...node.data.nodeInputs };
        }
      });
      
      // Update the nodeInputs state with the synchronized values
      setNodeInputs(syncedInputs);
    }
    
    // Update the previous action state
    previousActionRef.current = { isSaving, isTesting };
  }, [isSaving, isTesting, nodes, nodeInputs, setNodeInputs]);
};

// Custom hook to debounce ResizeObserver
const useDebouncedResizeObserver = (ref, callback, delay = 200) => {
  const observer = useRef(null);

  const debouncedCallback = useCallback(
    debounce((entries) => callback(entries), delay),
    [callback, delay]
  );

  useEffect(() => {
    if (!ref.current) return;

    observer.current = new ResizeObserver(debouncedCallback);
    observer.current.observe(ref.current);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [ref, debouncedCallback]);

  return observer;
};

const originalConsoleError = console.error;
console.error = (...args) => {
  const errorMsg = args[0]?.toString?.() || '';
  if (
    errorMsg.includes('ResizeObserver') || 
    errorMsg.includes('Maximum update depth exceeded') ||
    errorMsg.includes('ResizeObserver loop completed with undelivered notifications') ||
    errorMsg.includes('ResizeObserver loop limit exceeded')
  ) {
    // Suppress these specific errors
    return;
  }
  originalConsoleError(...args);
};

// Add this to index.js or App.js (outside of any component):
// This adds a global error handler for ResizeObserver errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event.message.includes('ResizeObserver') || 
      event.error?.message?.includes('ResizeObserver')
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      console.warn('ResizeObserver error suppressed');
    }
  });
}

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const callChatCompletion = async (llm, messages, apiKey, temperature) => {
  console.log('Calling chat completion with API key:', apiKey);
  
  // Map the display names to the actual API model names
  let model;
  switch (llm) {
    case 'GPT-4':
      model = 'gpt-4';
      break;
    case 'GPT-4o':
      model = 'gpt-4o';
      break;
    case 'GPT-4o-mini':
      model = 'gpt-4o-mini';
      break;
    default:
      model = 'gpt-3.5-turbo'; // Default for GPT-3.5-turbo or any other option
  }
  
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, messages, temperature: Number(temperature) }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message || 'API error');
  return data.choices[0].message.content;
};

const FlowWrapper = ({ children }) => {
  const wrapperRef = useRef(null);

  const handleResize = useCallback((entries) => {
    // Force a re-render or layout recalculation only when necessary
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      // Optional: Trigger a state update or layout recalc if needed
      console.log(`FlowWrapper resized to ${width}x${height}`);
    }
  }, []);

  useDebouncedResizeObserver(wrapperRef, handleResize, 200);

  return (
    <div 
      ref={wrapperRef}
      style={{ 
        height: 'calc(100vh - 60px)', 
        width: '100%', 
        position: 'relative'
      }}
    >
      {children}
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [nodeInputs, setNodeInputs] = useState({});
  const [flowName, setFlowName] = useState('');
  const [savedFlows, setSavedFlows] = useState([]);
  const [isAddNodeOpen, setIsAddNodeOpen] = useState(false);
  const [tab, setTab] = useState('home');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [flowSearch, setFlowSearch] = useState('');
  const [isNewFlowModalOpen, setIsNewFlowModalOpen] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);
  const flowWrapperRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const debounceTimeout = useRef(null);
  const initialEffectRun = useRef(false);
  const [editingWorkspaceName, setEditingWorkspaceName] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewApiKey, setShowNewApiKey] = useState(null);
  const [tools, setTools] = useState([]);
  const [flushUpdatesFlag, setFlushUpdatesFlag] = useState(false);
  const [isEditingTool, setIsEditingTool] = useState(false);
  const [currentEditingTool, setCurrentEditingTool] = useState(null);
  const [flowVariables, setFlowVariables] = useState({});

  const handleSpawnToolNodes = useCallback((newToolNodes, newEdges) => {
    setNodes(nds => [
      ...nds.filter(n => !n.data.isToolCall || !newToolNodes.some(tn => tn.id === n.id)),
      ...newToolNodes
    ]);
    setEdges(eds => [
      ...eds.filter(e => !e.source.startsWith(newToolNodes[0]?.data.parentNodeId + '-tool-')),
      ...newEdges
    ]);
  }, []);

  const cleanupVirtualElements = useCallback(() => {
    console.log("Cleaning up virtual elements...");
    setNodes(nds => nds.filter(n => !n.data?.isVirtual));
    setEdges(eds => eds.filter(e => !e.isVirtual));
  }, []);

  const updateTool = async (toolId, updatedTool) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('tools')
        .update({
          name: updatedTool.name,
          description: updatedTool.description || '',
          function_name: updatedTool.function_name,
          function_description: updatedTool.function_description || '',
          function_parameters: updatedTool.function_parameters || {},
          url: updatedTool.url
        })
        .eq('id', toolId)
        .select()
        .single();
        
      if (error) {
        console.error('Error updating tool:', error);
        toast.error('Failed to update tool!');
        return false;
      }
      
      // Update the tools state with the updated tool
      setTools(prev => prev.map(t => t.id === toolId ? data : t));
      toast.success('Tool updated successfully');
      return true;
    } catch (e) {
      console.error('Exception updating tool:', e);
      toast.error('Failed to update tool!');
      return false;
    } finally {
      setIsLoading(false);
    }
  };


  const flushAllNodes = () => {
    return new Promise((resolve) => {
      // Trigger flush
      setFlushUpdatesFlag(true);
      // Update all nodes with the flush flag
      setNodes(prevNodes => 
        prevNodes.map(node => ({
          ...node,
          data: { ...node.data, flushUpdatesFlag: true }
        }))
      );
      // Wait briefly for updates to propagate
      setTimeout(() => {
        setFlushUpdatesFlag(false);
        resolve();
      }, 150); // 150ms ensures debounced updates (100ms) complete
    });
  };

  const getBorderColor = (node) => {
    if (!node.data?.isConnected) return '#ff4d4f';
    switch (node.data?.status) {
      case 'processing': return '#faad14';
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      default: return '#6941c6';
    }
  };

  const nodeTypes = useMemo(() => ({ 
    custom: (props) => <CustomNode {...props} credentials={credentials} tools={tools} />,
    toolCall: ToolCallNode
  }), [credentials, tools]);

  const fetchTools = async (userId) => {
    console.log('Fetching tools for user:', userId);
    try {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching tools:', error);
        toast.error('Failed to load tools');
        return [];
      }
      
      console.log('Successfully fetched tools:', data);
      setTools(data || []);
      return data || [];
    } catch (e) {
      console.error('Exception fetching tools:', e);
      toast.error('Failed to load tools');
      return [];
    }
  };

  const fetchApiKeys = async (workspaceId) => {
    if (!workspaceId) return;
    
    try {
      const { data, error } = await supabase
        .from('workspace_api_keys')
        .select('id, name, created_at, last_used')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error fetching API keys:', error);
        return;
      }
      
      setApiKeys(data || []);
    } catch (err) {
      console.error('Exception fetching API keys:', err);
    }
  };

  const updateWorkspaceName = async () => {
    if (!selectedWorkspace || !newWorkspaceName.trim()) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({ name: newWorkspaceName.trim() })
        .eq('id', selectedWorkspace.id);
        
      if (error) {
        toast.error('Failed to update workspace name');
        console.error('Error updating workspace name:', error);
      } else {
        // Update local state
        setWorkspaces(prev => prev.map(w => 
          w.id === selectedWorkspace.id ? { ...w, name: newWorkspaceName.trim() } : w
        ));
        setSelectedWorkspace(prev => ({ ...prev, name: newWorkspaceName.trim() }));
        setEditingWorkspaceName(false);
        toast.success('Workspace name updated');
      }
    } catch (err) {
      console.error('Exception updating workspace name:', err);
      toast.error('Failed to update workspace name');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialEffectRun.current) {
      return; // Skip if already run
    }
    
    initialEffectRun.current = true;
    
    console.log("Initial auth effect running...");
    
    // Parse URL params once
    const urlParams = new URLSearchParams(window.location.search);
    const flowId = urlParams.get('flow');
    if (flowId) setSelectedFlowId(flowId);
  
    // Function to always ensure loading state is updated
    const checkAndUpdateLoadingState = (user) => {
      console.log("Checking auth state:", user ? "User logged in" : "No user");
      
      if (user) {
        // User is logged in, wait for data to load
        Promise.all([
          fetchWorkspaces(user.id),
          fetchCredentials(user.id),
          fetchTools(user.id) // Add this
        ]).finally(() => {
          console.log("Data fetching complete, setting loading to false");
          setIsLoading(false);
        });
      } else {
        // No user, no need to wait
        console.log("No user, setting loading to false immediately");
        setIsLoading(false);
      }
    };
  
    // Set up auth listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      setUser(session?.user || null);
      checkAndUpdateLoadingState(session?.user);
    });
    
    // Get initial session
    supabase.auth.getSession().then(({ data }) => {
      console.log("Initial auth session:", data.session ? "Has session" : "No session");
      setUser(data.session?.user || null);
      checkAndUpdateLoadingState(data.session?.user);
    });
    
    // Cleanup
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []); // Empty dependency array
  useEffect(() => {
    if (chatMessagesRef.current) {
      const scrollTimeout = setTimeout(() => {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }, 50); // Small timeout to ensure content has rendered
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [chatMessages.length]); // Only depend on the length, not the entire array

  useEffect(() => {
    // Update all existing nodes with new credentials whenever credentials change
    if (credentials.length > 0) {
      console.log('Credentials changed, updating nodes:', credentials);
      setNodes(prevNodes => 
        prevNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            credentials: credentials
          }
        }))
      );
    }
  }, [credentials]);

  useEffect(() => {
    if (selectedWorkspace && tab === 'flows' && selectedFlowId) {
      // Use a debounce to prevent multiple loads if these values change rapidly
      const loadTimer = setTimeout(() => {
        console.log('Triggering loadFlow for flowId:', selectedFlowId);
        loadFlow(selectedFlowId);
      }, 100);
      
      return () => clearTimeout(loadTimer);
    }
  }, [selectedWorkspace?.id, tab, selectedFlowId]); // Include .id to avoid unnecessary re-renders

  const handleSignIn = async () => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) toast.error(error.message);
    else { setEmail(''); setPassword(''); }
    setIsLoading(false);
  };

  const handleSignUp = async () => {
    setIsLoading(true);
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) toast.error(error.message);
    else { toast.success('Sign up successful! Check your email.'); setEmail(''); setPassword(''); setIsSignUp(false); }
    setIsLoading(false);
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    await supabase.auth.signOut();
    setUser(null); setWorkspaces([]); setSelectedWorkspace(null); setNodes([]); setEdges([]);
    setNodeInputs({}); setChatMessages([]); setSelectedFlowId(null); setCredentials([]);
    window.history.pushState({}, '', '/');
    setIsLoading(false);
  };

  const fetchWorkspaces = async (userId) => {
    setIsLoading(true);
    const { data: ownedWorkspaces, error: ownedError } = await supabase.from('workspaces').select('*').eq('owner_id', userId);
    const { data: memberWorkspaces, error: memberError } = await supabase
      .from('workspace_members')
      .select('workspace_id')
      .eq('user_id', userId)
      .then(async ({ data, error }) => {
        if (error) return { data: [], error };
        const workspaceIds = data.map((wm) => wm.workspace_id);
        if (!workspaceIds.length) return { data: [], error: null };
        return await supabase.from('workspaces').select('*').in('id', workspaceIds);
      });

    if (ownedError || memberError) {
      console.error('Error fetching workspaces:', ownedError || memberError);
      toast.error('Failed to load workspaces');
      setIsLoading(false);
      return;
    }

    const allWorkspaces = [...(ownedWorkspaces || []), ...(memberWorkspaces || [])];
    const uniqueWorkspaces = Array.from(new Map(allWorkspaces.map(ws => [ws.id, ws])).values());
    setWorkspaces(uniqueWorkspaces);
    if (uniqueWorkspaces.length > 0 && !selectedWorkspace) {
      setSelectedWorkspace(uniqueWorkspaces[0]);
      fetchFlows(uniqueWorkspaces[0].id);
    }
    setIsLoading(false);
  };

  const createWorkspace = async (name) => {
    if (!name) { toast.error('Workspace name required!'); return; }
    setIsLoading(true);
    const { data, error } = await supabase.from('workspaces').insert({ name, owner_id: user.id }).select().single();
    if (error) toast.error('Failed to create workspace');
    else { setWorkspaces((prev) => [...prev, data]); setSelectedWorkspace(data); fetchFlows(data.id); }
    setIsLoading(false);
  };

  const fetchFlows = async (workspaceId) => {
    console.log('Fetching flows for workspace:', workspaceId);
    const { data, error } = await supabase.from('flows').select('*').eq('workspace_id', workspaceId);
    if (error) { console.error('Error fetching flows:', error); toast.error('Failed to load flows'); }
    else { console.log('Fetched flows:', data); setSavedFlows(data); }
  };

  const fetchCredentials = async (userId) => {
    console.log('Fetching credentials for user:', userId);
    try {
      const { data, error } = await supabase
        .from('credentials')
        .select('id, name, value')
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching credentials:', error);
        toast.error('Failed to load credentials');
        return [];
      }
      
      console.log('Successfully fetched credentials:', data);
      setCredentials(data || []);
      return data || [];
    } catch (e) {
      console.error('Exception fetching credentials:', e);
      toast.error('Failed to load credentials');
      return [];
    }
  };

  const createFlow = () => { setTab('flows'); setIsNewFlowModalOpen(true); };

  const handleCreateFlow = async () => {
    if (!newFlowName.trim()) { toast.error('Flow name required!'); return; }
    setIsLoading(true);
    const startNode = {
      id: 'start-node',
      type: 'custom',
      data: { ...nodeTypesConfig.start, label: nodeTypesConfig.start.label, status: 'idle', onNodeUpdate: handleNodeUpdate, onDelete: handleDeleteNode, onDuplicate: handleDuplicateNode, nodeInputs: {}, isConnected: true },
      position: { x: 250, y: 50 },
    };
    const flowData = { workspace_id: selectedWorkspace.id, name: newFlowName, nodes: [startNode], edges: [], inputs: {} };
    console.log('Creating flow with data:', flowData);
    const { data, error } = await supabase.from('flows').insert(flowData).select().single();
    if (error) { console.error('Error creating flow:', error); toast.error('Failed to create flow!'); setIsLoading(false); return; }
    console.log('Created flow:', data);
    setNodes([startNode]); setEdges([]); setNodeInputs({}); setFlowName(newFlowName); setSelectedFlowId(data.id);
    setTab('flows'); setNewFlowName(''); setIsNewFlowModalOpen(false);
    window.history.pushState({}, '', `?flow=${data.id}`);
    fetchFlows(selectedWorkspace.id);
    setIsLoading(false);
  };

  const addTool = async (tool) => {
    if (!tool.name || !tool.function_name || !tool.url) {
      toast.error('Tool name, function name, and URL are required!');
      return null;
    }
    
    setIsLoading(true);
    try {
      const newTool = {
        user_id: user.id,
        name: tool.name,
        description: tool.description || '',
        function_name: tool.function_name,
        function_description: tool.function_description || '',
        function_parameters: tool.function_parameters || {},
        url: tool.url
      };
      
      const { data, error } = await supabase
        .from('tools')
        .insert(newTool)
        .select()
        .single();
        
      if (error) {
        console.error('Error adding tool:', error);
        toast.error('Failed to add tool!');
        return null;
      }
      
      setTools(prev => [...prev, data]);
      toast.success('Tool added successfully');
      return data;
    } catch (e) {
      console.error('Exception adding tool:', e);
      toast.error('Failed to add tool!');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTool = async (toolId) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('tools')
        .delete()
        .eq('id', toolId);
        
      if (error) {
        console.error('Error deleting tool:', error);
        toast.error('Failed to delete tool!');
        return false;
      }
      
      setTools(prev => prev.filter(t => t.id !== toolId));
      toast.success('Tool deleted');
      return true;
    } catch (e) {
      console.error('Exception deleting tool:', e);
      toast.error('Failed to delete tool!');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const addNode = (nodeType, inputs = {}) => {
    const newNodeId = `${Date.now()}`;
    const nodeConfig = nodeTypesConfig[nodeType];
    
    // Ensure default inputs are set
    const defaultInputs = {};
    if (nodeConfig.inputs) {
      nodeConfig.inputs.forEach(input => {
        if (input.default !== undefined) {
          defaultInputs[input.id] = input.default;
        }
      });
    }
    
    // Add default settings
    if (nodeConfig.settings) {
      defaultInputs.settings = { ...nodeConfig.settings };
    }
    
    // Merge with any provided inputs
    const mergedInputs = { ...defaultInputs, ...inputs };
    
    const newNode = {
      id: newNodeId,
      type: 'custom',
      data: { 
        ...nodeConfig, 
        label: nodeConfig.label, 
        status: 'idle', 
        onNodeUpdate: handleNodeUpdate, 
        onDelete: handleDeleteNode, 
        onDuplicate: handleDuplicateNode, 
        nodeInputs: mergedInputs,
        isConnected: false,
        // Add credentials and tools references
        credentials: credentials,
        tools: tools
      },
      position: { x: Math.random() * 500, y: Math.random() * 500 },
    };
    
    setNodes((nds) => {
      const updatedNodes = [...nds, newNode];
      updateNodeConnectivity(updatedNodes, edges);
      return updatedNodes;
    });
    
    // Always set nodeInputs for new nodes, even if empty
    setNodeInputs((prev) => ({ ...prev, [newNodeId]: mergedInputs }));
    setIsAddNodeOpen(false);
  };

  const handleNodeUpdate = useCallback((nodeId, inputs) => {
    setNodeInputs(prev => {
      const newInputs = { ...prev, [nodeId]: { ...inputs } };
      setNodes(prevNodes =>
        prevNodes.map(n =>
          n.id === nodeId ? { ...n, data: { ...n.data, nodeInputs: newInputs[nodeId], flushUpdatesFlag } } : n
        )
      );
      return newInputs;
    });
  }, [flushUpdatesFlag]);
  

  const handleDeleteNode = (nodeId) => {
    debouncedSetNodes((nds) => {
      const updatedNodes = nds.filter((n) => n.id !== nodeId);
      setEdges((eds) => {
        const updatedEdges = eds.filter((e) => e.source !== nodeId && e.target !== nodeId);
        updateNodeConnectivity(updatedNodes, updatedEdges);
        return updatedEdges;
      });
      return updatedNodes;
    });
    setNodeInputs((prev) => { const updated = { ...prev }; delete updated[nodeId]; return updated; });
  };

  const updateNodeConnectivity = useCallback((updatedNodes = nodes, updatedEdges = edges) => {
    console.log('Updating node connectivity');
    setNodes(currentNodes => updateNodesConnectivityStatus(currentNodes, updatedEdges));
  }, [nodes, edges]);
  
  // Helper function to update connectivity status (unchanged, but included for context)
  const updateNodesConnectivityStatus = (currentNodes, currentEdges) => {
    const connectedNodes = new Set();
    const startNode = currentNodes.find((n) => n.id === 'start-node');
    
    if (startNode) {
      connectedNodes.add(startNode.id);
      
      const traverse = (nodeId) => {
        currentEdges.forEach((edge) => {
          if (edge.source === nodeId && !connectedNodes.has(edge.target)) {
            connectedNodes.add(edge.target);
            traverse(edge.target);
          }
        });
      };
      
      traverse(startNode.id);
    }
    
    return currentNodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isConnected: connectedNodes.has(node.id)
      }
    }));
  };
  
  // Debounced setNodes (move this up too, since it's related)
  const debouncedSetNodes = useCallback((newNodesUpdater) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    debounceTimeout.current = setTimeout(() => {
      setNodes((nds) => {
        const newNodes = typeof newNodesUpdater === 'function' 
          ? newNodesUpdater(nds) 
          : newNodesUpdater;
        return updateNodesConnectivityStatus(newNodes, edges);
      });
    }, 250);
  }, [edges]);
  
  useEffect(() => {
    debouncedSetNodesRef.current = debouncedSetNodes;
  }, [debouncedSetNodes]);

  // In App.js, update the handleDuplicateNode function:

  const handleDuplicateNode = useCallback((nodeId, additionalData = {}) => {
    console.log(`Duplicating node ${nodeId}`, additionalData);
    
    try {
      // Find the node to duplicate
      const nodeToDuplicate = nodes.find((n) => n.id === nodeId);
      if (!nodeToDuplicate) {
        console.error(`Cannot duplicate: node with ID ${nodeId} not found in`, nodes.map(n => n.id));
        toast.error('Unable to find node to duplicate');
        return;
      }
      
      // Generate a unique ID for the new node
      const newNodeId = `node-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      
      console.log('Found node to duplicate:', nodeToDuplicate);
  
      // Determine which node type config to use based on the label
      const nodeTypeKey = Object.keys(nodeTypesConfig).find(
        key => nodeTypesConfig[key].label === nodeToDuplicate.data.label
      );
  
      if (!nodeTypeKey) {
        console.error(`Cannot duplicate: node type for ${nodeToDuplicate.data.label} not found`);
        toast.error('Invalid node type');
        return;
      }
      
      // Get the existing inputs for this node
      const existingInputs = { ...nodeInputs[nodeId] } || {};
      
      // Create the new node with proper offset from original
      const offset = 80; // Visual offset for the duplicated node
      
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position: {
          x: nodeToDuplicate.position.x + offset,
          y: nodeToDuplicate.position.y + offset
        },
        data: {
          ...nodeTypesConfig[nodeTypeKey],
          label: nodeTypesConfig[nodeTypeKey].label,
          onNodeUpdate: handleNodeUpdate,
          onDelete: handleDeleteNode,
          onDuplicate: handleDuplicateNode,
          status: 'idle',
          isConnected: false,
          nodeInputs: existingInputs,
          credentials: credentials,
          tools: tools // Include tools if you have them
        }
      };
      
      console.log('Created new duplicated node:', newNode);
      
      // Add the node to nodes state
      setNodes(prevNodes => {
        const updatedNodes = [...prevNodes, newNode];
        return updatedNodes;
      });
      
      // Add inputs to nodeInputs state
      setNodeInputs(prevInputs => ({
        ...prevInputs,
        [newNodeId]: existingInputs
      }));
      
      // Update node connectivity after a delay to ensure state updates have propagated
      setTimeout(() => {
        updateNodeConnectivity();
      }, 100);
      
      toast.success('Node duplicated successfully');
    } catch (error) {
      console.error('Error during node duplication:', error);
      toast.error(`Failed to duplicate node: ${error.message}`);
    }
  }, [nodes, nodeInputs, nodeTypesConfig, credentials, tools, handleNodeUpdate, handleDeleteNode, updateNodeConnectivity]);





  const processNode = async (node, input) => {
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          let output = input;
          const inputs = nodeInputs[node.id] || {};
          console.log(`Processing node ${node.id} (${node.data.label}) with inputs:`, inputs);
  
          // Set node status to processing
          setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'processing', lastInput: input[input.length - 1]?.content || 'N/A' } } : n)));
  
          switch (node.data.label) {
            case 'Start': 
              output = input; 
              break;
  
              case 'Chat Completion':
  try {
    const { 
      provider = 'OpenAI', 
      llm = 'GPT-4o', 
      prompt = '', 
      temperature = 0.7, 
      maxTokens = 1024, 
      apiKey = '', 
      tools: nodeTools = [],
      settings = {}
    } = inputs;

    if (!apiKey) throw new Error('API Key required for Chat Completion');

    // Format messages with fine-tuning examples (unchanged)
    let messages = [];
    if (prompt) messages.push({ role: 'system', content: prompt });
    // ... existing fine-tuning examples logic ...

    const examples = inputs.examples || [];
              if (examples && examples.length > 0) {
                console.log('Adding fine-tuning examples:', examples);
                
                // Insert a separator before examples
                if (messages.length > 0) {
                  messages.push({ 
                    role: 'system', 
                    content: 'Below are example conversations showing the desired behavior:' 
                  });
                }
                
                // Add each example conversation
                examples.forEach(example => {
                  if (example.messages && example.messages.length > 0) {
                    // Add a header for this example if there's a title
                    if (example.title) {
                      messages.push({ 
                        role: 'system', 
                        content: `Example: ${example.title}` 
                      });
                    }
                    
                    // Add all messages from this example
                    example.messages.forEach(msg => {
                      messages.push({
                        role: msg.role,
                        content: msg.content
                      });
                    });
                    
                    // Add a separator between examples
                    messages.push({ 
                      role: 'system', 
                      content: '---' 
                    });
                  }
                });
                
                // Add a transition to the current conversation
                messages.push({ 
                  role: 'system', 
                  content: 'Now, please respond to the following conversation:' 
                });
              }

    messages = [...messages, ...input];
    const processedMessages = messages.map(msg => ({
      ...msg,
      content: typeof msg.content === 'string' ? substituteVariables(msg.content, flowVariables) : msg.content
    }));

    const options = {
      maxTokens: Number(maxTokens),
      responseFormat: settings.responseFormat === 'json' ? 'json_object' : 'text'
    };

    if (nodeTools.length > 0 && settings.spawnToolNodes) {
      console.log('Spawning tool nodes for:', nodeTools);

      const nodePosition = node.position || { x: 0, y: 0 };
      const newToolNodes = [];
      const newEdges = [];

      // Create tool nodes and edges
      nodeTools.forEach((tool, index) => {
        const toolNodeId = `${node.id}-tool-${tool.id || index}`;
        const posX = nodePosition.x + 350;
        const posY = nodePosition.y + (index - (nodeTools.length - 1) / 2) * 120;

        const toolNode = {
          id: toolNodeId,
          type: 'toolCall',
          position: { x: posX, y: posY },
          data: {
            label: tool.name,
            tool: tool,
            args: null, // Will be populated when tool is called
            status: 'idle',
            isToolCall: true,
            result: null,
            error: null,
            executionTime: null,
            parentNodeId: node.id // Track parent for return flow
          }
        };

        const edgeToTool = {
          id: `edge-${node.id}-to-${toolNodeId}`,
          source: node.id,
          target: toolNodeId,
          animated: true,
          style: { stroke: '#6941c6' }
        };

        newToolNodes.push(toolNode);
        newEdges.push(edgeToTool);
      });

      // Add tool nodes and edges to state
      setNodes(nds => [...nds.filter(n => !n.data.isToolCall || n.data.parentNodeId !== node.id), ...newToolNodes]);
      setEdges(eds => [...eds.filter(e => !e.source.startsWith(`${node.id}-tool-`)), ...newEdges]);

      // Update outputs to include tool nodes
      node.data.outputs = ['response', ...newToolNodes.map(tn => tn.id)];
    }

    // Make initial API call
    const response = await callChatCompletion(provider, llm, processedMessages, apiKey, Number(temperature), options);

    if (response && response.tool_calls && response.tool_calls.length > 0) {
      const toolCalls = response.tool_calls;
      const assistantMessage = { role: 'assistant', ...response };
      let conversation = [...input, assistantMessage];

      for (const toolCall of toolCalls) {
        const tool = nodeTools.find(t => t.function.name === toolCall.function.name);
        if (!tool) {
          console.error(`Tool not found: ${toolCall.function.name}`);
          continue;
        }

        const toolNodeId = `${node.id}-tool-${tool.id || toolCall.id}`;
        const toolNode = nodes.find(n => n.id === toolNodeId);

        if (toolNode) {
          setNodes(nds => nds.map(n => n.id === toolNodeId ? {
            ...n,
            data: { ...n.data, status: 'processing', args: JSON.parse(toolCall.function.arguments) }
          } : n));

          try {
            const startTime = Date.now();
            const toolResponse = await fetch(tool.url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: tool.function.name,
                args: JSON.parse(toolCall.function.arguments),
                meta_data: { nodeId: node.id, flowId: selectedFlowId }
              })
            });

            const executionTime = Date.now() - startTime;
            if (!toolResponse.ok) throw new Error(`Tool API error: ${toolResponse.statusText}`);

            const toolResult = await toolResponse.json();

            setNodes(nds => nds.map(n => n.id === toolNodeId ? {
              ...n,
              data: {
                ...n.data,
                status: 'success',
                result: toolResult,
                executionTime: `${executionTime}ms`
              }
            } : n));

            const returnEdgeId = `edge-${toolNodeId}-to-${node.id}`;
            setEdges(eds => [...eds.filter(e => e.id !== returnEdgeId), {
              id: returnEdgeId,
              source: toolNodeId,
              target: node.id,
              animated: true,
              style: { stroke: '#16a34a' }
            }]);

            conversation.push({
              role: 'tool',
              tool_call_id: toolCall.id,
              content: JSON.stringify(toolResult)
            });
          } catch (toolError) {
            setNodes(nds => nds.map(n => n.id === toolNodeId ? {
              ...n,
              data: {
                ...n.data,
                status: 'error',
                error: toolError.message
              }
            } : n));

            const returnEdgeId = `edge-${toolNodeId}-to-${node.id}`;
            setEdges(eds => [...eds.filter(e => e.id !== returnEdgeId), {
              id: returnEdgeId,
              source: toolNodeId,
              target: node.id,
              animated: true,
              style: { stroke: '#ff4d4f' }
            }]);

            conversation.push({
              role: 'tool',
              tool_call_id: toolCall.id,
              content: JSON.stringify({ error: toolError.message })
            });
          }
        }
      }

      const followUpResponse = await callChatCompletion(provider, llm, conversation, apiKey, Number(temperature), options);
      conversation.push({ role: 'assistant', content: followUpResponse });
      output = conversation;
    } else {
      output = typeof response === 'string' 
        ? [...input, { role: 'assistant', content: response }]
        : [...input, { role: 'assistant', content: response.content || JSON.stringify(response) }];
    }
  } catch (error) {
    console.error('Error in Chat Completion node:', error);
    setNodes(nds => nds.map(n => n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n));
    reject(new Error(`Chat Completion error: ${error.message}`));
    return;
  }
  break;
              
            case 'API Request':
              try {
                console.log('Processing API Request node with input:', input);
                
                // Extract configuration from node inputs
                const { 
                  requestConfig = { method: 'GET', url: '' },
                  headers: headersList = [],
                  body: bodyStr = '',
                  parseResponse = 'Yes',
                  timeout = 10000,
                  retries = 0
                } = inputs;
                
                const method = requestConfig.method || 'GET';
                const url = requestConfig.url || '';
                
                if (!url) {
                  throw new Error('API URL is required');
                }
                
                // Convert headers array to object
                let headers = {};
                headersList.forEach(header => {
                  if (header.key && header.value) {
                    headers[header.key] = header.value;
                  }
                });
                
                // Set default content-type if not provided
                if (!headers['Content-Type'] && !headers['content-type'] && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
                  headers['Content-Type'] = 'application/json';
                }
                
                // Prepare request options
                const options = {
                  method,
                  headers,
                  signal: AbortSignal.timeout(Number(timeout) || 10000)
                };
                
                // Add body for non-GET requests if provided
                if (method !== 'GET' && bodyStr.trim()) {
                  options.body = bodyStr;
                }
                
                console.log(`Making ${method} request to ${url} with options:`, options);
                
                // Implementation of retry logic
                const fetchWithRetry = async (url, options, retriesLeft) => {
                  try {
                    const response = await fetch(url, options);
                    if (!response.ok) {
                      throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
                    }
                    return response;
                  } catch (error) {
                    if (retriesLeft > 0) {
                      console.log(`Retrying API request. Attempts left: ${retriesLeft - 1}`);
                      // Wait before retry (exponential backoff)
                      await new Promise(resolve => setTimeout(resolve, 1000 * (Math.pow(2, retries - retriesLeft + 1))));
                      return fetchWithRetry(url, options, retriesLeft - 1);
                    }
                    throw error;
                  }
                };
                
                // Make the API request with retry
                const response = await fetchWithRetry(url, options, Number(retries) || 0);
                
                // Process the response based on settings
                let responseData;
                if (parseResponse === 'Yes') {
                  try {
                    responseData = await response.json();
                  } catch (error) {
                    console.warn('Failed to parse response as JSON, using text');
                    responseData = await response.text();
                  }
                } else {
                  responseData = await response.text();
                }
                
                console.log('API Response:', responseData);
                
                // Format output for the flow
                let processedOutput;
                if (Array.isArray(input) && input.length > 0) {
                  // If we have previous messages, append the result to them
                  const apiResponseMessage = {
                    role: 'system',
                    content: `API Response: ${typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2)}`
                  };
                  
                  processedOutput = [...input, apiResponseMessage];
                } else {
                  // If there's no input, just create a new message with the API response
                  processedOutput = [{
                    role: 'system',
                    content: `API Response: ${typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2)}`
                  }];
                }
                
                output = processedOutput;
              } catch (error) {
                console.error('Error in API Request node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Handle error based on settings
                const errorHandling = inputs.settings?.errorHandling || 'continue';
                
                if (errorHandling === 'stop') {
                  reject(new Error(`API Request error: ${error.message}`));
                  return;
                } else if (errorHandling === 'fallback') {
                  // Use fallback data
                  output = [{
                    role: 'system',
                    content: 'Using fallback data due to API error.'
                  }];
                } else {
                  // Default: continue with error
                  if (Array.isArray(input)) {
                    output = [...input, { role: 'system', content: `API Request Error: ${error.message}` }];
                  } else {
                    output = [{ role: 'system', content: `API Request Error: ${error.message}` }];
                  }
                }
              }
              break;
  
            case 'Store Variable':
              try {
                console.log('Processing Store Variable node with input:', input);
                
                // Extract inputs
                const { 
                  variableName = '',
                  variableSource = { type: 'Last Message', value: '' }, 
                  scope = 'Conversation'
                } = inputs;
                
                if (!variableName) {
                  throw new Error('Variable name is required');
                }
                
                // Determine the value to store based on source
                let valueToStore = '';
                
                const sourceType = typeof variableSource === 'object' ? variableSource.type : 'Last Message';
                const sourceValue = typeof variableSource === 'object' ? variableSource.value : '';
                
                // If input is an array of messages (conversation)
                if (Array.isArray(input) && input.length > 0) {
                  if (sourceType === 'Last Message') {
                    // Extract content from the last message
                    valueToStore = input[input.length - 1].content || '';
                  } 
                  else if (sourceType === 'Message at Index') {
                    // Get message at specific index
                    const index = parseInt(sourceValue);
                    if (index >= 0 && index < input.length) {
                      valueToStore = input[index].content || '';
                    } else {
                      throw new Error(`Invalid message index: ${index}. Available range: 0-${input.length - 1}`);
                    }
                  }
                }
                
                // Use custom value if specified
                if (sourceType === 'Custom Value') {
                  valueToStore = sourceValue;
                }
                
                console.log(`Storing variable "${variableName}" with value:`, valueToStore);
                
                // Update the flowVariables state with the new variable
                setFlowVariables(prev => ({
                  ...prev,
                  [variableName]: valueToStore
                }));
                
                // Pass through the input unchanged
                output = input;
              } catch (error) {
                console.error('Error in Store Variable node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Pass through the input, but add an error message
                if (Array.isArray(input)) {
                  output = [...input, { role: 'system', content: `Store Variable Error: ${error.message}` }];
                } else {
                  output = [{ role: 'system', content: `Store Variable Error: ${error.message}` }];
                }
              }
              break;
                
            case 'Find and Replace':
              try {
                console.log('Processing Find and Replace node with input:', input);
                const { 
                  find = '', 
                  replace = '',
                  mode = 'Simple'
                } = inputs;
                
                // Skip processing if find string is empty
                if (!find) {
                  output = input;
                  break;
                }
                
                // Create regex pattern based on mode
                let pattern;
                let flags = 'g';
                
                if (mode === 'Regex') {
                  // Use find as is for regex mode (user is expected to provide a valid regex pattern)
                  try {
                    pattern = new RegExp(find, flags);
                  } catch (e) {
                    console.error('Invalid regex pattern:', e);
                    pattern = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags);
                  }
                } else if (mode === 'Case Sensitive') {
                  // Escape special regex characters but maintain case sensitivity
                  pattern = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags);
                } else {
                  // Simple mode - escape special characters and ignore case
                  pattern = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags + 'i');
                }
                
                // Defensive programming - handle all possible input types
                if (!input) {
                  // If input is null/undefined, return empty array
                  output = [];
                } else if (typeof input === 'string') {
                  // If input is a string, convert to a single-item array with proper structure
                  output = [{ role: 'user', content: input.replace(pattern, replace) }];
                } else if (Array.isArray(input)) {
                  // If it's already an array, process each item
                  output = input.map(msg => {
                    // Check if msg is an object with content property
                    if (msg && typeof msg === 'object' && 'content' in msg && typeof msg.content === 'string') {
                      return {
                        ...msg,
                        content: msg.content.replace(pattern, replace)
                      };
                    }
                    // If msg doesn't have expected structure, return it unchanged
                    return msg;
                  });
                } else if (typeof input === 'object') {
                  // If it's a single object, try to process it
                  if ('content' in input && typeof input.content === 'string') {
                    output = [{
                      ...input,
                      content: input.content.replace(pattern, replace)
                    }];
                  } else {
                    // Can't process, return as is
                    output = [input];
                  }
                } else {
                  // For any other type, just wrap in array
                  output = [input];
                }
                
                console.log('Find and Replace output:', output);
              } catch (error) {
                console.error('Error in Find and Replace node:', error);
                // Ensure we always return something valid even on error
                output = Array.isArray(input) ? input : (input ? [input] : []);
              }
              break;
                
            case 'Knowledge Base':
              try {
                console.log('Processing Knowledge Base node with input:', input);
                
                // Extract inputs
                const { 
                  openaiApiKey = '',
                  pineconeApiKey = '', 
                  pineconeEnvironment = '',
                  pineconeIndex = '',
                  pineconeNamespace = '',
                  topK = 3,
                  similarityThreshold = 0.7,
                  model = 'text-embedding-3-small',
                  includeMetadata = 'Yes'
                } = inputs;
                
                // Validate required inputs
                if (!openaiApiKey) throw new Error('OpenAI API Key is required');
                if (!pineconeApiKey) throw new Error('Pinecone API Key is required');
                if (!pineconeEnvironment) throw new Error('Pinecone Environment is required');
                if (!pineconeIndex) throw new Error('Pinecone Index Name is required');
                
                // Extract query from input
                let query = '';
                if (Array.isArray(input) && input.length > 0) {
                  const lastMessage = input[input.length - 1];
                  query = lastMessage.content || '';
                } else if (typeof input === 'string') {
                  query = input;
                } else {
                  throw new Error('No valid query found in input');
                }
                
                if (!query.trim()) {
                  throw new Error('Empty query');
                }
                
                console.log(`Creating embedding for query: "${query}"`);
                
                // Step 1: Create embedding for the query using OpenAI
                const embeddingResponse = await fetch('https://api.openai.com/v1/embeddings', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${openaiApiKey}`
                  },
                  body: JSON.stringify({
                    input: query,
                    model: model
                  })
                });
                
                if (!embeddingResponse.ok) {
                  const error = await embeddingResponse.json();
                  throw new Error(`OpenAI embedding failed: ${error.error?.message || 'Unknown error'}`);
                }
                
                const embeddingData = await embeddingResponse.json();
                const embedding = embeddingData.data[0].embedding;
                
                console.log('Embedding created successfully');
                
                // Step 2: Query Pinecone with the embedding
                const pineconeUrl = `https://${pineconeIndex}-${pineconeEnvironment}.svc.${pineconeEnvironment}.pinecone.io/query`;
                
                const pineconeBody = {
                  vector: embedding,
                  topK: Number(topK),
                  includeMetadata: includeMetadata === 'Yes',
                  namespace: pineconeNamespace || undefined,
                  includeValues: false
                };
                
                console.log('Querying Pinecone:', pineconeUrl);
                
                const pineconeResponse = await fetch(pineconeUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Api-Key': pineconeApiKey
                  },
                  body: JSON.stringify(pineconeBody)
                });
                
                if (!pineconeResponse.ok) {
                  const error = await pineconeResponse.text();
                  throw new Error(`Pinecone query failed: ${error}`);
                }
                
                const pineconeData = await pineconeResponse.json();
                console.log('Pinecone results:', pineconeData);
                
                // Step 3: Process and filter results based on similarity threshold
                const matches = pineconeData.matches || [];
                const filteredMatches = matches.filter(match => match.score >= Number(similarityThreshold));
                
                console.log(`Found ${filteredMatches.length} relevant matches above threshold ${similarityThreshold}`);
                
                // Step 4: Format results as context
                let contextText = '';
                
                if (filteredMatches.length > 0) {
                  contextText = filteredMatches.map((match, index) => {
                    let text = `[Source ${index + 1}]: ${match.metadata?.text || 'No text available'}`;
                    
                    if (includeMetadata === 'Yes' && match.metadata) {
                      const metadataWithoutText = { ...match.metadata };
                      delete metadataWithoutText.text;
                      
                      if (Object.keys(metadataWithoutText).length > 0) {
                        text += `\nMetadata: ${JSON.stringify(metadataWithoutText)}`;
                      }
                    }
                    
                    return text;
                  }).join('\n\n');
                } else {
                  contextText = "No relevant information found in the knowledge base.";
                }
                
                // Step 5: Create output message with context
                let processedOutput;
                if (Array.isArray(input)) {
                  // Add a system message with the retrieved context
                  const contextMessage = {
                    role: 'system',
                    content: `Knowledge Base Context:\n\n${contextText}`
                  };
                  
                  processedOutput = [...input, contextMessage];
                } else {
                  processedOutput = [
                    { role: 'user', content: query },
                    { role: 'system', content: `Knowledge Base Context:\n\n${contextText}` }
                  ];
                }
                
                output = processedOutput;
              } catch (error) {
                console.error('Error in Knowledge Base node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Still provide output but with the error
                if (Array.isArray(input)) {
                  output = [...input, { role: 'system', content: `Knowledge Base Error: ${error.message}` }];
                } else {
                  output = [{ role: 'system', content: `Knowledge Base Error: ${error.message}` }];
                }
              }
              break;
                
            case 'If/Else':
              try {
                const { operator = 'equals', value = '' } = inputs;
                
                // Settings
                const evaluateOnce = inputs.settings?.evaluateOnce || false;
                const nodeWasEvaluated = node.data.evaluated;
                
                // Skip evaluation if evaluateOnce is true and the node was already evaluated
                if (evaluateOnce && nodeWasEvaluated) {
                  console.log('Skipping If/Else evaluation (evaluateOnce is enabled)');
                  output = { value: input, conditionResult: node.data.lastResult };
                  break;
                }
                
                let result = false;
                const lastMessage = Array.isArray(input) && input.length > 0 ? 
                  input[input.length - 1]?.content || '' : 
                  (typeof input === 'string' ? input : '');
                
                switch (operator) {
                  case 'equals': result = lastMessage === value; break;
                  case 'does not equal': result = lastMessage !== value; break;
                  case 'is greater than': 
                    // Try numeric comparison first, then fall back to string length
                    const numLastMessage = Number(lastMessage);
                    const numValue = Number(value);
                    result = !isNaN(numLastMessage) && !isNaN(numValue) ? 
                      numLastMessage > numValue : 
                      lastMessage.length > Number(value); 
                    break;
                  case 'is less than': 
                    const numLM = Number(lastMessage);
                    const numVal = Number(value);
                    result = !isNaN(numLM) && !isNaN(numVal) ? 
                      numLM < numVal : 
                      lastMessage.length < Number(value); 
                    break;
                  case 'includes': result = lastMessage.includes(value); break;
                  case 'is empty': result = lastMessage === ''; break;
                  case 'is not empty': result = lastMessage !== ''; break;
                  case 'matches regex': 
                    try {
                      const regex = new RegExp(value);
                      result = regex.test(lastMessage);
                    } catch (e) {
                      console.error('Invalid regex pattern:', e);
                      result = false;
                    }
                    break;
                  default: result = false;
                }
                
                // Mark the node as evaluated and store the result for future reference
                if (evaluateOnce) {
                  setNodes((nds) => nds.map((n) => (n.id === node.id ? 
                    { ...n, data: { ...n.data, evaluated: true, lastResult: result } } : n
                  )));
                }
                
                output = { value: input, conditionResult: result };
              } catch (error) {
                console.error('Error in If/Else node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Default to false on error
                output = { value: input, conditionResult: false };
              }
              break;
                
            case 'AI Decision':
              // Use apiKey to match nodeTypesConfig
              try {
                const { 
                  apiKey: decisionApiKey = '', 
                  model = 'GPT-4o',
                  instruction = '',
                  outcomes = [{ condition: 'Fallback', id: 'fallback' }] 
                } = inputs;
                
                // Settings
                const confidence = inputs.settings?.confidence || 0.7;
                const fallbackBehavior = inputs.settings?.fallbackBehavior || 'default';
                
                // Debug the API key to find the issue
                console.log('AI Decision node processing with API key:', decisionApiKey ? 'Present (hidden)' : 'Missing');
                console.log('Node inputs:', inputs);
                console.log('Outcomes:', outcomes);
                
                // Better error handling
                if (!decisionApiKey || decisionApiKey.trim() === '') {
                  setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                  toast.error('AI Decision needs an API key!');
                  throw new Error('API Key required for AI Decision');
                }
                
                // Format all input messages for context
                const allMessagesContext = Array.isArray(input) ? 
                  input.map(msg => `${msg.role}: ${msg.content}`).join('\n') : 
                  'No conversation context available';
                
                // Add instruction if provided
                const instructionText = instruction ? 
                  `${instruction}\n\n` : 
                  `Evaluate the following conversation and determine which outcome best matches. Respond ONLY with the ID of the matching outcome.\n\n`;
                
                const decisionPrompt = `${instructionText}Conversation:\n\n${allMessagesContext}\n\nPossible outcomes (respond ONLY with the ID):\n${outcomes.map((o) => `- ID: ${o.id}, Condition: ${o.condition}`).join('\n')}\n\nIf confidence is below ${confidence * 100}%, return "fallback".`;
                
                // More robust promise handling
                try {
                  const messages = [{ role: 'user', content: decisionPrompt }];
                  const result = await callChatCompletion(
                    'OpenAI', 
                    model, 
                    messages, 
                    decisionApiKey, 
                    0.1, // Low temperature for more deterministic results
                    { maxTokens: 50, responseFormat: 'text' } // We need just a short answer
                  );
                  
                  // Set node status to success
                  setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'success' } } : n)));
                  
                  // Extract the decision ID from the response, cleaning up any extra text
                  const decisionId = result.trim().split(/[\s\n]/)[0];
                  
                  // Check if the decision matches any outcome
                  const matchedOutcome = outcomes.find((o) => o.id === decisionId);
                  
                  if (matchedOutcome) {
                    output = { value: input, decisionId: matchedOutcome.id };
                  } else {
                    // Fall back to the fallback outcome
                    const fallbackOutcome = outcomes.find((o) => o.id === 'fallback');
                    output = { value: input, decisionId: fallbackOutcome?.id || 'fallback' };
                  }
                } catch (error) {
                  // Handle error based on fallbackBehavior setting
                  console.error('Error in AI Decision node:', error);
                  setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                  
                  if (fallbackBehavior === 'retry' && !node.data.retried) {
                    // Mark as retried to prevent infinite loops
                    setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, retried: true } } : n)));
                    console.log('Retrying AI Decision node...');
                    // The retry logic would go here in a real implementation
                    // For now, we'll just fall back to the fallback outcome
                    const fallbackOutcome = outcomes.find((o) => o.id === 'fallback');
                    output = { value: input, decisionId: fallbackOutcome?.id || 'fallback' };
                  } else {
                    // Use fallback path
                    const fallbackOutcome = outcomes.find((o) => o.id === 'fallback');
                    output = { value: input, decisionId: fallbackOutcome?.id || 'fallback' };
                  }
                }
              } catch (error) {
                console.error('Error in AI Decision node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Default to fallback on error
                output = { value: input, decisionId: 'fallback' };
              }
              break;
  
            case 'User Input':
              try {
                const {
                  prompt = '',
                  inputType = 'text',
                  options = [],
                  validation = 'none',
                  validationRule = ''
                } = inputs;
                
                // Settings
                const timeoutSeconds = inputs.settings?.timeoutSeconds || 120;
                const timeoutBehavior = inputs.settings?.timeoutBehavior || 'reminder';
                
                // In a real system, we would wait for user input here
                // For now, we'll simulate receiving input from the chat system
                console.log('User Input node processing. Prompt:', prompt);
                
                // For testing, we'll just pass through the input
                // In a real system, we would add a prompt message and wait for user response
                if (Array.isArray(input)) {
                  // Add prompt as system message if not empty
                  if (prompt) {
                    output = [...input, { role: 'system', content: prompt }];
                  } else {
                    output = input;
                  }
                } else {
                  output = [{ role: 'system', content: prompt || 'Please provide input' }];
                }
                
                // Signal that this node requires user input
                // In a real implementation, you would set a flag to wait for user input
                // and return output when it's received
              } catch (error) {
                console.error('Error in User Input node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                if (Array.isArray(input)) {
                  output = [...input, { role: 'system', content: `User Input Error: ${error.message}` }];
                } else {
                  output = [{ role: 'system', content: `User Input Error: ${error.message}` }];
                }
              }
              break;
              
            case 'Output':
              try {
                const { message = '', formatOutput = 'None' } = inputs;
                
                // Settings
                const markConversationComplete = inputs.settings?.markConversationComplete || true;
                
                let finalOutput;
                if (Array.isArray(input)) {
                  if (message) {
                    // Append static message if provided
                    finalOutput = [...input, { role: 'assistant', content: message }];
                  } else {
                    finalOutput = input;
                  }
                } else if (typeof input === 'string') {
                  finalOutput = [{ role: 'assistant', content: input }];
                  if (message) {
                    finalOutput.push({ role: 'assistant', content: message });
                  }
                } else {
                  finalOutput = [{ role: 'assistant', content: 'No content to display' }];
                  if (message) {
                    finalOutput.push({ role: 'assistant', content: message });
                  }
                }
                
                // Format the output if requested
                if (formatOutput !== 'None' && Array.isArray(finalOutput) && finalOutput.length > 0) {
                  const lastMessage = finalOutput[finalOutput.length - 1];
                  let formattedContent = lastMessage.content;
                  
                  if (formatOutput === 'JSON') {
                    try {
                      // If already JSON, prettify it
                      const parsedJson = JSON.parse(formattedContent);
                      formattedContent = JSON.stringify(parsedJson, null, 2);
                    } catch {
                      // Not JSON, mark output as unformatted
                      formattedContent = `Note: Could not format as JSON. Original content:\n\n${formattedContent}`;
                    }
                  } else if (formatOutput === 'Markdown') {
                    // No processing needed for Markdown, it's passed as-is
                  } else if (formatOutput === 'HTML') {
                    // For HTML, we might want to sanitize, but we'll just pass it through for now
                  }
                  
                  // Update the last message with formatted content
                  finalOutput[finalOutput.length - 1] = {
                    ...lastMessage,
                    content: formattedContent
                  };
                }
                
                // In a real system, if markConversationComplete is true, 
                // you might want to signal that the conversation is complete
                
                output = finalOutput;
              } catch (error) {
                console.error('Error in Output node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                if (Array.isArray(input)) {
                  output = [...input, { role: 'system', content: `Output Error: ${error.message}` }];
                } else {
                  output = [{ role: 'system', content: `Output Error: ${error.message}` }];
                }
              }
              break;
              
            case 'Sentiment':
              try {
                const { provider = 'Built-in', apiKey = '' } = inputs;
                
                // Settings
                const detailedAnalysis = inputs.settings?.detailedAnalysis || false;
                
                // Get the text to analyze (from last message)
                let textToAnalyze = '';
                if (Array.isArray(input) && input.length > 0) {
                  textToAnalyze = input[input.length - 1].content || '';
                } else if (typeof input === 'string') {
                  textToAnalyze = input;
                } else {
                  textToAnalyze = '';
                }
                
                if (!textToAnalyze.trim()) {
                  throw new Error('No text to analyze');
                }
                
                let sentiment = 'neutral';
                let confidenceScores = { positive: 0, neutral: 0, negative: 0 };
                
                if (provider === 'OpenAI') {
                  if (!apiKey) {
                    throw new Error('API Key required for OpenAI sentiment analysis');
                  }
                  
                  const promptText = detailedAnalysis ?
                    `Analyze the sentiment of the following text. Provide a detailed analysis including the overall sentiment (positive, neutral, or negative) and confidence scores for each category. Only respond with JSON in the format: {"sentiment": "positive|neutral|negative", "scores": {"positive": 0.X, "neutral": 0.X, "negative": 0.X}, "analysis": "brief explanation"}\n\nText: ${textToAnalyze}` :
                    `Analyze the sentiment of the following text. Is it positive, neutral, or negative? Only respond with a single word: positive, neutral, or negative.\n\nText: ${textToAnalyze}`;
                  
                  const response = await callChatCompletion(
                    'OpenAI',
                    'GPT-4o',
                    [{ role: 'user', content: promptText }],
                    apiKey,
                    0.1,
                    { responseFormat: detailedAnalysis ? 'json_object' : 'text' }
                  );
                  
                  if (detailedAnalysis) {
                    try {
                      const jsonResponse = typeof response === 'string' ? JSON.parse(response) : response;
                      sentiment = jsonResponse.sentiment;
                      confidenceScores = jsonResponse.scores;
                    } catch (e) {
                      console.error('Error parsing sentiment analysis JSON:', e);
                      sentiment = 'neutral';
                    }
                  } else {
                    // Extract sentiment from the simple response
                    const responseLower = response.toLowerCase().trim();
                    if (responseLower.includes('positive')) {
                      sentiment = 'positive';
                    } else if (responseLower.includes('negative')) {
                      sentiment = 'negative';
                    } else {
                      sentiment = 'neutral';
                    }
                  }
                } else {
                  // Built-in sentiment analysis (simple keyword-based approach)
                  // In a real system, you'd want a more sophisticated approach
                  const positiveWords = ['good', 'great', 'excellent', 'happy', 'love', 'enjoy', 'like', 'best', 'wonderful', 'fantastic'];
                  const negativeWords = ['bad', 'terrible', 'awful', 'hate', 'dislike', 'worst', 'horrible', 'poor', 'disappointed', 'angry'];
                  
                  const text = textToAnalyze.toLowerCase();
                  let positiveCount = 0;
                  let negativeCount = 0;
                  
                  positiveWords.forEach(word => {
                    const regex = new RegExp(`\\b${word}\\b`, 'gi');
                    const matches = text.match(regex);
                    if (matches) positiveCount += matches.length;
                  });
                  
                  negativeWords.forEach(word => {
                    const regex = new RegExp(`\\b${word}\\b`, 'gi');
                    const matches = text.match(regex);
                    if (matches) negativeCount += matches.length;
                  });
                  
                  if (positiveCount > negativeCount) {
                    sentiment = 'positive';
                    confidenceScores = { positive: 0.7, neutral: 0.2, negative: 0.1 };
                  } else if (negativeCount > positiveCount) {
                    sentiment = 'negative';
                    confidenceScores = { positive: 0.1, neutral: 0.2, negative: 0.7 };
                  } else {
                    sentiment = 'neutral';
                    confidenceScores = { positive: 0.3, neutral: 0.4, negative: 0.3 };
                  }
                }
                
                // Store the result for routing
                output = { 
                  value: input, 
                  sentiment, 
                  scores: confidenceScores,
                  source: provider
                };
                
                // Mark the node with the detected sentiment for visualization
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { 
                  ...n, 
                  data: { 
                    ...n.data, 
                    status: 'success',
                    sentimentResult: sentiment 
                  } 
                } : n)));
              } catch (error) {
                console.error('Error in Sentiment node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                // Default to neutral on error
                output = { value: input, sentiment: 'neutral', error: error.message };
              }
              break;
              
            case 'Entity Extraction':
              try {
                const { 
                  entities = [], 
                  provider = 'OpenAI',
                  apiKey = ''
                } = inputs;
                
                // Settings
                const storeEntities = inputs.settings?.storeEntities || true;
                const storePrefix = inputs.settings?.storePrefix || 'entity.';
                
                if (entities.length === 0) {
                  throw new Error('No entities specified for extraction');
                }
                
                // Get the text to analyze (from last message)
                let textToAnalyze = '';
                if (Array.isArray(input) && input.length > 0) {
                  textToAnalyze = input[input.length - 1].content || '';
                } else if (typeof input === 'string') {
                  textToAnalyze = input;
                } else {
                  textToAnalyze = '';
                }
                
                if (!textToAnalyze.trim()) {
                  throw new Error('No text to analyze for entity extraction');
                }
                
                let extractedEntities = {};
                
                if (provider === 'OpenAI') {
                  if (!apiKey) {
                    throw new Error('API Key required for OpenAI entity extraction');
                  }
                  
                  const entitiesList = entities.map(e => typeof e === 'string' ? e : e.name || e.entity).join(', ');
                  
                  const promptText = `Extract the following entities from the text: ${entitiesList}.\n\nRespond only with a JSON object where keys are entity names and values are the extracted values. If an entity is not found, set its value to null.\n\nText: ${textToAnalyze}`;
                  
                  const response = await callChatCompletion(
                    'OpenAI',
                    'GPT-4o',
                    [{ role: 'user', content: promptText }],
                    apiKey,
                    0.1,
                    { responseFormat: 'json_object' }
                  );
                  
                  try {
                    extractedEntities = typeof response === 'string' ? JSON.parse(response) : response;
                  } catch (e) {
                    console.error('Error parsing entity extraction JSON:', e);
                    extractedEntities = {};
                  }
                } else {
                  // Built-in entity extraction (simple regex-based approach)
                  // In a real system, you'd want a more sophisticated approach
                  entities.forEach(entity => {
                    const entityName = typeof entity === 'string' ? entity : entity.name || entity.entity;
                    const pattern = typeof entity === 'object' && entity.pattern ? entity.pattern : '\\b' + entityName + '\\s*[:\\-=]\\s*([\\w\\s]+)';
                    
                    try {
                      const regex = new RegExp(pattern, 'i');
                      const match = textToAnalyze.match(regex);
                      if (match && match[1]) {
                        extractedEntities[entityName] = match[1].trim();
                      } else {
                        extractedEntities[entityName] = null;
                      }
                    } catch (e) {
                      console.error(`Error with regex pattern for entity ${entityName}:`, e);
                      extractedEntities[entityName] = null;
                    }
                  });
                }
                
                // Store entities in flowVariables if requested
                if (storeEntities) {
                  const newVariables = {};
                  Object.entries(extractedEntities).forEach(([entity, value]) => {
                    if (value !== null) {
                      newVariables[`${storePrefix}${entity}`] = value;
                    }
                  });
                  
                  setFlowVariables(prev => ({
                    ...prev,
                    ...newVariables
                  }));
                }
                
                // Add extraction results to the conversation
                let processedOutput;
                if (Array.isArray(input)) {
                  // Add a system message with the extraction results
                  const extractionMessage = {
                    role: 'system',
                    content: `Entity Extraction Results: ${JSON.stringify(extractedEntities, null, 2)}`
                  };
                  
                  processedOutput = [...input, extractionMessage];
                } else {
                  processedOutput = [
                    { role: 'system', content: textToAnalyze },
                    { role: 'system', content: `Entity Extraction Results: ${JSON.stringify(extractedEntities, null, 2)}` }
                  ];
                }
                
                // Store extraction results for routing
                output = { 
                  value: processedOutput, 
                  success: Object.values(extractedEntities).some(v => v !== null),
                  entities: extractedEntities
                };
              } catch (error) {
                console.error('Error in Entity Extraction node:', error);
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                
                if (Array.isArray(input)) {
                  output = { 
                    value: [...input, { role: 'system', content: `Entity Extraction Error: ${error.message}` }],
                    success: false,
                    entities: {}
                  };
                } else {
                  output = { 
                    value: [{ role: 'system', content: `Entity Extraction Error: ${error.message}` }],
                    success: false,
                    entities: {}
                  };
                }
              }
              break;
              
            case 'Memory Manager':
              // Implementation for Memory Manager (placeholder for now)
              // Add your own implementation based on your requirements
              output = input;
              break;
              
            default: 
              console.log(`No specific handler for node type: ${node.data.label}, passing input through`);
              output = input;
          }
          
          // Handle promise or regular value in a consistent way
          if (output instanceof Promise) {
            output
              .then(result => {
                // Set node status to success
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'success' } } : n)));
                resolve(result);
              })
              .catch(error => {
                // Set node status to error
                setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
                reject(error);
              });
          } else {
            // Set node status to success for immediate (non-promise) outputs
            setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'success' } } : n)));
            resolve(output);
          }
        } catch (error) {
          // Set node status to error
          setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, status: 'error' } } : n)));
          console.error('Error in processNode:', error);
          reject(error);
        }
      }, 300); // Shortened timeout for better UX
    });
  };

  const runTest = async (messages) => {
    if (!nodes.length || !messages.length) return;
    
    setIsTesting(true);
    try {
      // Flush all node updates and wait
      await flushAllNodes();
      
      // Ensure nodeInputs is fully updated
      const syncedInputs = { ...nodeInputs };
      nodes.forEach(node => {
        if (node.data && node.data.nodeInputs) {
          syncedInputs[node.id] = { ...node.data.nodeInputs };
        }
      });
      setNodeInputs(syncedInputs);
  
      const updatedNodes = nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          nodeInputs: syncedInputs[node.id] || {},
          flushUpdatesFlag: false, // Reset flag
          status: 'idle'
        }
      }));
      setNodes(updatedNodes);
  
      const startNode = updatedNodes.find((n) => n.id === 'start-node');
      if (!startNode) { 
        toast.error('Start node missing!');
        throw new Error('Start node missing');
      }
  
      const processFlow = async (nodeId, input) => {
        const node = updatedNodes.find((n) => n.id === nodeId);
        if (!node) return input;
  
        const processMessageWithVariables = (message) => {
          if (typeof message.content === 'string') {
            return {
              ...message,
              content: substituteVariables(message.content, flowVariables)
            };
          }
          return message;
        };
  
        const processedInput = Array.isArray(input) 
          ? input.map(processMessageWithVariables)
          : input;
  
        // Check if the node is global and skip it if needed
        const nodeSettings = node.data?.nodeInputs?.settings || {};
        if (nodeSettings.skipResponse) {
          console.log(`Skipping node ${nodeId} (${node.data.label}) due to skipResponse setting`);
          // Process the node but don't wait for user response
          processNode(node, processedInput).catch(err => console.error(err));
          
          // Find the next edge and continue the flow
          const nextEdges = edges.filter((e) => e.source === nodeId);
          if (nextEdges.length > 0) {
            const nextEdge = nextEdges[0]; // Take first outgoing edge
            return await processFlow(nextEdge.target, processedInput); // Pass the same input
          }
          return processedInput; // No outgoing edges, return the input
        }
  
        console.log(`Processing node ${nodeId} (${node.data.label}) with inputs:`, syncedInputs[nodeId]);
        const output = await processNode(node, processedInput);
        console.log(`Node ${nodeId} output:`, output);
  
        const nextEdges = edges.filter((e) => e.source === nodeId);
        if (!nextEdges.length) return output;
  
        let nextEdge;
        if (node.data.label === 'If/Else') {
          const conditionResult = output.conditionResult;
          console.log(`If/Else condition result: ${conditionResult}`);
          nextEdge = nextEdges.find((e) => e.sourceHandle === (conditionResult ? 'true' : 'false')) || nextEdges[0];
        } else if (node.data.label === 'AI Decision') {
          const decisionId = output.decisionId;
          console.log(`AI Decision result: ${decisionId}`);
          nextEdge = nextEdges.find((e) => e.sourceHandle === decisionId) || 
                   nextEdges.find((e) => e.sourceHandle === 'fallback') || 
                   nextEdges[0];
        } else {
          nextEdge = nextEdges[0];
        }
  
        return await processFlow(nextEdge.target, output.value || output);
      };
  
      // Find any global nodes that should be pre-processed
      const globalNodes = updatedNodes.filter(node => {
        const settings = node.data?.nodeInputs?.settings || {};
        return settings.globalNode === true;
      });
      
      if (globalNodes.length > 0) {
        console.log(`Found ${globalNodes.length} global nodes to pre-process:`, globalNodes.map(n => n.id));
      }
  
      console.log('Starting test with messages:', messages);
      console.log('Using synchronized inputs:', syncedInputs);
      const finalOutput = await processFlow(startNode.id, messages);
      console.log('Test completed with output:', finalOutput);
  
      if (typeof finalOutput === 'string') {
        setChatMessages((prev) => [...prev, { role: 'assistant', content: finalOutput }]);
      } else if (Array.isArray(finalOutput)) {
        setChatMessages((prev) => [...prev, { 
          role: 'assistant', 
          content: finalOutput[finalOutput.length - 1]?.content || 'No output' 
        }]);
      } else if (finalOutput && typeof finalOutput === 'object') {
        if (finalOutput.value && Array.isArray(finalOutput.value)) {
          setChatMessages((prev) => [...prev, { 
            role: 'assistant', 
            content: finalOutput.value[finalOutput.value.length - 1]?.content || 'No output' 
          }]);
        } else {
          setChatMessages((prev) => [...prev, { 
            role: 'assistant', 
            content: JSON.stringify(finalOutput) 
          }]);
        }
      }
    } catch (error) {
      console.error('Test failed:', error);
      toast.error(`Test failed: ${error.message}`);
    } finally {
      // Set a timeout to clean up virtual elements after test completion
      // The timeout ensures user can see the final state for a moment
      setTimeout(() => {
        cleanupVirtualElements();
        setIsTesting(false);
      }, 2000);
    }
  };

  const callChatCompletion = async (provider, model, messages, apiKey, temperature, options = {}) => {
    console.log(`Calling ${provider} chat completion with model: ${model}`);
    
    switch (provider) {
      case 'OpenAI':
        return callOpenAIChatCompletion(model, messages, apiKey, temperature, options);
      case 'Anthropic':
        return callAnthropicChatCompletion(model, messages, apiKey, temperature, options);
      case 'Cohere':
        return callCohereChatCompletion(model, messages, apiKey, temperature, options);
      case 'Grok':
        return callGrokChatCompletion(model, messages, apiKey, temperature, options);
      case 'Mixtral':
        return callMixtralChatCompletion(model, messages, apiKey, temperature, options);
      default:
        return callOpenAIChatCompletion(model, messages, apiKey, temperature, options); // Default to OpenAI
    }
  };
  
  const callOpenAIChatCompletion = async (model, messages, apiKey, temperature, options = {}) => {
    // Map the display names to the actual API model names
    let apiModel;
    switch (model) {
      case 'GPT-4':
        apiModel = 'gpt-4';
        break;
      case 'GPT-4o':
        apiModel = 'gpt-4o';
        break;
      case 'GPT-4o-mini':
        apiModel = 'gpt-4o-mini';
        break;
      default:
        apiModel = 'gpt-3.5-turbo'; // Default for GPT-3.5-turbo or any other option
    }
    
    const requestBody = { 
      model: apiModel, 
      messages, 
      temperature: Number(temperature)
    };
    
    // Add any additional options
    if (options.responseFormat) {
      requestBody.response_format = { type: options.responseFormat };
    }
    
    if (options.tools && options.tools.length > 0) {
      requestBody.tools = options.tools;
    }
    
    if (options.maxTokens) {
      requestBody.max_tokens = Number(options.maxTokens);
    }
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error?.message || 'API error');
    return data.choices[0].message.content;
  };
  
  const callAnthropicChatCompletion = async (model, messages, apiKey, temperature, options = {}) => {
    // Convert messages format to Anthropic format
    const anthropicMessages = messages.map(msg => {
      // Anthropic uses 'human' and 'assistant' roles instead of 'user' and 'assistant'
      const role = msg.role === 'user' ? 'human' : msg.role === 'assistant' ? 'assistant' : 'system';
      return { role, content: msg.content };
    });
    
    // Map model names to Anthropic format
    let apiModel;
    switch (model) {
      case 'Claude 3 Opus':
        apiModel = 'claude-3-opus-20240229';
        break;
      case 'Claude 3 Sonnet':
        apiModel = 'claude-3-sonnet-20240229';
        break;
      case 'Claude 3 Haiku':
        apiModel = 'claude-3-haiku-20240307';
        break;
      default:
        apiModel = 'claude-3-sonnet-20240229'; // Default
    }
    
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: apiModel,
        messages: anthropicMessages,
        max_tokens: options.maxTokens || 1024,
        temperature: Number(temperature)
      }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error?.message || 'Anthropic API error');
    return data.content[0].text;
  };
  
  // Add more provider-specific implementations as needed
  const callCohereChatCompletion = async (model, messages, apiKey, temperature, options = {}) => {
    // Implementation for Cohere
    // Placeholder for now
    return "Cohere integration to be implemented";
  };
  
  const callGrokChatCompletion = async (model, messages, apiKey, temperature, options = {}) => {
    // Implementation for Grok
    // Placeholder for now
    return "Grok integration to be implemented";
  };
  
  const callMixtralChatCompletion = async (model, messages, apiKey, temperature, options = {}) => {
    // Implementation for Mixtral
    // Placeholder for now
    return "Mixtral integration to be implemented";
  };

  const handleSendMessage = () => {
    if (!userInput.trim()) return;
    const newMessage = { role: 'user', content: userInput.trim() };
    setChatMessages((prev) => [...prev, newMessage]);
    setUserInput('');
    runTest([...chatMessages, newMessage]);
  };

  const handleResetChat = () => { setChatMessages([]); setUserInput(''); };

  const saveFlow = async () => {
    if (!flowName || !selectedWorkspace) { 
      toast.error('Enter a flow name and select a workspace!'); 
      return; 
    }
    
    setIsLoading(true);
    try {
      // Flush all node updates and wait
      await flushAllNodes();
      
      // Ensure nodeInputs is fully updated
      const syncedInputs = { ...nodeInputs };
      nodes.forEach(node => {
        if (node.data && node.data.nodeInputs) {
          syncedInputs[node.id] = { ...node.data.nodeInputs };
        }
      });
      setNodeInputs(syncedInputs); // Update state with latest inputs
  
      const flowData = {
        id: selectedFlowId,
        workspace_id: selectedWorkspace.id,
        name: flowName,
        nodes: nodes.map((node) => ({
          ...node,
          data: { 
            ...node.data, 
            onNodeUpdate: undefined, 
            onDelete: undefined, 
            onDuplicate: undefined,
            flushUpdatesFlag: undefined, // Clean up flag
            nodeInputs: syncedInputs[node.id] || {}
          }
        })),
        edges,
        inputs: syncedInputs,
        variables: flowVariables // Save variables with the flow
      };
      
      console.log('Saving flow with synchronized data:', flowData);
      
      const { data, error } = await supabase
        .from('flows')
        .upsert(flowData, { onConflict: 'id' })
        .select()
        .single();
      
      if (error) {
        console.error('Error saving flow:', error);
        throw error;
      }
      
      toast.success('Flow saved successfully!');
      fetchFlows(selectedWorkspace.id);
    } catch (err) {
      console.error('Exception in saveFlow:', err);
      toast.error('Failed to save flow: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  

  const loadFlow = async (flowId) => {
    setIsLoading(true);
    
    try {
      console.log('Loading flow with ID:', flowId);
      console.log('Current credentials:', credentials);
      
      const { data, error } = await supabase
        .from('flows')
        .select('nodes, edges, inputs, name, variables')
        .eq('id', flowId)
        .single();
        
      if (error) {
        console.error('Error loading flow:', error);
        toast.error('Failed to load flow: ' + error.message);
        setIsLoading(false);
        return;
      }
      
      console.log('Loaded flow data:', data);
      
      // Load the saved variables, if any
      setFlowVariables(data.variables || {});
      
      // First extract all inputs from both sources
      let combinedNodeInputs = { ...(data.inputs || {}) };
      
      // Also collect inputs embedded in nodes
      if (data.nodes && Array.isArray(data.nodes)) {
        data.nodes.forEach(node => {
          if (node.data && node.data.nodeInputs) {
            combinedNodeInputs[node.id] = {
              ...(combinedNodeInputs[node.id] || {}), 
              ...node.data.nodeInputs
            };
          }
        });
      }
      
      console.log('Combined node inputs:', combinedNodeInputs);
      
      // Now update the nodes with all callbacks and the proper inputs
      const updatedNodes = (data.nodes || []).map((node) => ({
        ...node,
        data: {
          ...node.data,
          onNodeUpdate: handleNodeUpdate,
          onDelete: handleDeleteNode,
          onDuplicate: handleDuplicateNode,
          nodeInputs: combinedNodeInputs[node.id] || {},
          isConnected: false,
          // Add credentials to each node
          credentials: credentials
        },
      }));
      
      console.log('Mapped nodes with credentials:', updatedNodes);
      
      // Set all state in one batch to avoid multiple renders
      setNodes(updatedNodes);
      setEdges(data.edges || []);
      setNodeInputs(combinedNodeInputs);
      setFlowName(data.name || '');
      setSelectedFlowId(flowId);
      
      // Update URL
      window.history.pushState({}, '', `?flow=${flowId}`);
      
      // Update connectivity after a small delay to ensure nodes and edges are set
      setTimeout(() => {
        updateNodeConnectivity();
      }, 100);
      
      toast.success('Flow loaded successfully!');
    } catch (err) {
      console.error('Exception loading flow:', err);
      toast.error('Failed to load flow: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFlow = async (flowId) => {
    setIsLoading(true);
    const { error } = await supabase.from('flows').delete().eq('id', flowId);
    if (error) toast.error('Failed to delete flow!');
    else {
      if (selectedFlowId === flowId) { setNodes([]); setEdges([]); setNodeInputs({}); setFlowName(''); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }
      toast.success('Flow deleted!');
      fetchFlows(selectedWorkspace.id);
    }
    setIsLoading(false);
  };

  const debouncedSetNodesRef = useRef(null);

  useEffect(() => {
    debouncedSetNodesRef.current = debouncedSetNodes;
  }, [debouncedSetNodes]);


  const onConnect = (params) => {
    setEdges((eds) => {
      const newEdges = addEdge(params, eds);
      updateNodeConnectivity(nodes, newEdges);
      return newEdges;
    });
  };

  const onNodesChange = useCallback((changes) => {
    // Skip debouncing for position changes to make dragging more responsive
    const positionChanges = changes.filter(change => change.type === 'position');
    if (positionChanges.length > 0) {
      setNodes((nds) => applyNodeChanges(positionChanges, nds));
    }
    
    // Use debounce only for non-position changes (like selections)
    const otherChanges = changes.filter(change => change.type !== 'position');
    if (otherChanges.length > 0) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        setNodes((nds) => applyNodeChanges(otherChanges, nds));
      }, 50); // Reduced from 100ms to 50ms
    }
  }, []);

  const addCredential = async (name, value) => {
    if (!name || !value) { toast.error('Credential name and value required!'); return; }
    setIsLoading(true);
    const { data, error } = await supabase.from('credentials').insert({ user_id: user.id, name, value }).select().single();
    if (error) { console.error('Error adding credential:', error); toast.error('Failed to add credential!'); }
    else { 
      setCredentials((prev) => [...prev, data]); 
      toast.success('Credential added!'); 
      fetchCredentials(user.id); // Refresh creds
    }
    setIsLoading(false);
  };

  const renderTabContent = () => {
    switch (tab) {
      case 'home':
        return (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            padding: '40px',
            textAlign: 'center' 
          }}>
            <img src="/icon.png" alt="Flowbuilder.ai" style={{ width: '80px', height: '80px', marginBottom: '24px' }} />
            <h2 style={{ 
              fontSize: '28px', 
              color: '#1f1f1f', 
              marginBottom: '16px',
              fontWeight: '600' 
            }}>
              Hi, {selectedWorkspace?.name || 'Your Workspace'} 👋
            </h2>
            <p style={{ fontSize: '16px', color: '#4b5563', maxWidth: '500px', lineHeight: '1.5' }}>
              Build and deploy AI agents via flows with a simple, powerful interface. Create flows, call the API, and deliver intelligent results.
            </p>
            <button 
              onClick={createFlow} 
              style={{ 
                marginTop: '32px', 
                padding: '12px 24px', 
                background: '#6941c6', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontSize: '16px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                fontWeight: '500',
                transition: 'all 0.2s',
                boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg> 
              Create Flow
            </button>
          </div>
        );
        
      case 'flows':
        if (!selectedFlowId) {
          return (
            <div style={{ padding: '24px 32px', height: '100%', overflowY: 'auto' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '24px' 
              }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f1f1f' }}>Flows</h2>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={flowSearch}
                      onChange={(e) => setFlowSearch(e.target.value)}
                      placeholder="Search flows..."
                      style={{ 
                        padding: '10px 12px 10px 38px', 
                        borderRadius: '8px', 
                        border: '1px solid #e0e0e0', 
                        fontSize: '14px', 
                        width: '240px',
                        background: '#fff',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
                      onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
                    />
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        position: 'absolute',
                        left: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af'
                      }}
                    >
                      <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 21L16.65 16.65" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <button
                    onClick={createFlow}
                    style={{ 
                      padding: '10px 16px', 
                      background: '#6941c6', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      fontSize: '14px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    New Flow
                  </button>
                </div>
              </div>
              
              {savedFlows.length === 0 ? (
                <div style={{ 
                  background: '#fff', 
                  borderRadius: '12px', 
                  padding: '60px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  color: '#6b7280'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H9M15 12H19M9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#374151', margin: '16px 0 8px' }}>No flows yet</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', maxWidth: '400px', marginBottom: '24px' }}>Create your first flow to start building AI-powered workflows and automate your processes.</p>
                  <button
                    onClick={createFlow}
                    style={{ 
                      padding: '10px 20px', 
                      background: '#6941c6', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      fontSize: '14px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontWeight: '500'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Create New Flow
                  </button>
                </div>
              ) : (
                <div style={{ 
                  background: '#fff', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Runs</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Errors</th>
                        <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedFlows
                        .filter((flow) => flow.name.toLowerCase().includes(flowSearch.toLowerCase()))
                        .map((flow) => (
                          <tr key={flow.id} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                            <td 
                              style={{ 
                                padding: '16px', 
                                fontSize: '14px', 
                                color: '#6941c6', 
                                cursor: 'pointer',
                                fontWeight: '500' 
                              }} 
                              onClick={() => setSelectedFlowId(flow.id)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 12H9M15 12H19M9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {flow.name}
                              </div>
                            </td>
                            <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>{new Date(flow.created_at).toLocaleDateString()}</td>
                            <td style={{ padding: '16px', fontSize: '14px', color: '#1f1f1f', textAlign: 'center' }}>{flow.runs || 0}</td>
                            <td style={{ padding: '16px', fontSize: '14px', color: flow.errors > 0 ? '#ef4444' : '#6b7280', textAlign: 'center', fontWeight: flow.errors > 0 ? '500' : 'normal' }}>{flow.errors || 0}</td>
                            <td style={{ padding: '16px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(flow.id);
                                    toast.success('Flow ID copied to clipboard');
                                  }} 
                                  title="Copy ID"
                                  style={{ 
                                    background: '#f9fafb', 
                                    border: '1px solid #e0e0e0', 
                                    color: '#6b7280', 
                                    borderRadius: '6px', 
                                    width: '32px', 
                                    height: '32px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteFlow(flow.id);
                                  }} 
                                  title="Delete flow"
                                  style={{ 
                                    background: '#fff1f0', 
                                    border: '1px solid #ffccc7', 
                                    color: '#ff4d4f', 
                                    borderRadius: '6px', 
                                    width: '32px', 
                                    height: '32px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6H5H21" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {isNewFlowModalOpen && (
                <div style={{ 
                  position: 'fixed', 
                  top: 0, 
                  left: 0, 
                  width: '100vw', 
                  height: '100vh', 
                  background: 'rgba(0, 0, 0, 0.5)', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  zIndex: 1002,
                  backdropFilter: 'blur(2px)'
                }}>
                  <div style={{ 
                    width: '420px', 
                    padding: '24px', 
                    background: '#fff', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)', 
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '20px'
                    }}>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: '#1f1f1f', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px' 
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Create New Flow
                      </div>
                      <button 
                        onClick={() => { setIsNewFlowModalOpen(false); setNewFlowName(''); }} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#9ca3af', 
                          cursor: 'pointer', 
                          width: '32px', 
                          height: '32px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          borderRadius: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
                        onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ 
                        fontSize: '14px', 
                        color: '#4b5563', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        Flow Name
                      </label>
                      <input 
                        type="text" 
                        value={newFlowName} 
                        onChange={(e) => setNewFlowName(e.target.value)} 
                        placeholder="Enter flow name" 
                        style={{ 
                          width: '100%', 
                          padding: '12px 14px', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px', 
                          fontSize: '14px', 
                          boxSizing: 'border-box',
                          transition: 'all 0.2s'
                        }} 
                        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
                        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
                        autoFocus
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                      <button 
                        onClick={() => { setIsNewFlowModalOpen(false); setNewFlowName(''); }} 
                        style={{ 
                          padding: '10px 16px', 
                          background: '#f9fafb', 
                          color: '#4b5563', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px', 
                          cursor: 'pointer', 
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleCreateFlow} 
                        style={{ 
                          padding: '10px 16px', 
                          background: '#6941c6', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: '8px', 
                          cursor: 'pointer', 
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
                        }}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }
        return (
          <div style={{ position: 'relative', height: '100%' }}>
            {/* Flow Editor Header - Fixed and aligned with sidebar */}
            <div style={{ 
  height: '60px', 
  width: '100%',            // Full width 
  borderBottom: '1px solid #f0f0f0', 
  padding: '0 24px', 
  background: '#fff', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'space-between', 
  position: 'absolute',     // Absolute positioning
  top: 0,                   // Aligned to top
  left: 0,                  // Aligned to left
  right: 0,                 // Stretched to right
  zIndex: 100,
  boxSizing: 'border-box',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
}}>
  {/* Header content remains the same */}
  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 auto' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12H9M15 12H19M9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <input 
        type="text" 
        value={flowName} 
        onChange={(e) => setFlowName(e.target.value)} 
        placeholder="Flow name" 
        style={{ 
          padding: '8px 12px', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px', 
          fontSize: '14px', 
          minWidth: '200px',
          fontWeight: '500',
          transition: 'all 0.2s'
        }} 
        disabled={isTesting}
        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
      />
    </div>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <button 
      onClick={saveFlow} 
      disabled={isTesting} 
      style={{ 
        padding: '8px 16px', 
        background: '#6941c6', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '8px', 
        cursor: isTesting ? 'not-allowed' : 'pointer', 
        fontSize: '14px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        fontWeight: '500',
        transition: 'all 0.2s',
        opacity: isTesting ? 0.7 : 1,
        boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 21H5C4.44772 21 4 20.5523 4 20V6C4 5.44772 4.44772 5 5 5H15L20 10V20C20 20.5523 19.5523 21 19 21Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15 5V10H20" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Save
    </button>
    <button 
      onClick={() => { setNodes([]); setEdges([]); setNodeInputs({}); setFlowName(''); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
      disabled={isTesting} 
      style={{ 
        padding: '8px 16px', 
        background: '#f9fafb', 
        color: '#4b5563', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        cursor: isTesting ? 'not-allowed' : 'pointer', 
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s',
        opacity: isTesting ? 0.7 : 1
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12H5" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 19L5 12L12 5" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Back
    </button>
  </div>
</div>
  
            {/* Flow actions floating toolbar */}
            <div style={{ 
              position: 'fixed', 
              bottom: '20px', 
              left: '50%', 
              transform: 'translateX(-50%)', 
              zIndex: 1000, 
              display: 'flex', 
              gap: '16px',
              background: '#fff',
              padding: '12px 20px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid #f0f0f0'
            }}>
              <button 
                onClick={() => setIsAddNodeOpen(true)} 
                disabled={isTesting} 
                style={{ 
                  padding: '10px 16px', 
                  background: '#6941c6', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: isTesting ? 'not-allowed' : 'pointer', 
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  opacity: isTesting ? 0.7 : 1,
                  boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add Node
              </button>
              <button 
                onClick={() => setIsChatDrawerOpen(true)} 
                disabled={isTesting} 
                style={{ 
                  padding: '10px 16px', 
                  background: '#f0fdf4', 
                  color: '#16a34a', 
                  border: '1px solid #dcfce7', 
                  borderRadius: '8px', 
                  cursor: isTesting ? 'not-allowed' : 'pointer', 
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  opacity: isTesting ? 0.7 : 1
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12L10 17L19 8" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Test Flow
              </button>
            </div>
  
            {isAddNodeOpen && (
  <div style={{ 
    position: 'fixed', 
    bottom: '82px', 
    left: '50%', 
    transform: 'translateX(-50%)', 
    width: '280px', 
    maxHeight: '380px', 
    background: '#fff', 
    border: '1px solid #f0f0f0', 
    borderRadius: '12px', 
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', 
    zIndex: 1001, 
    overflowY: 'auto'
  }}>
    <div style={{ 
      fontSize: '14px', 
      fontWeight: '600', 
      padding: '16px', 
      borderBottom: '1px solid #f0f0f0', 
      color: '#1f1f1f',
      position: 'sticky',
      top: 0,
      background: '#fff',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <span>Add Node</span>
      <button 
        onClick={() => setIsAddNodeOpen(false)} 
        style={{ 
          background: 'none', 
          border: 'none', 
          color: '#9ca3af', 
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
        onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
    <div style={{ padding: '8px' }}>
      {Object.entries(nodeTypesConfig)
        .filter(([key]) => key !== 'start') // Filter out the start node
        .map(([key, config]) => (
        <div 
          key={key} 
          onClick={() => addNode(key)} 
          style={{ 
            padding: '12px 16px', 
            fontSize: '14px', 
            color: '#4b5563', 
            cursor: 'pointer', 
            borderRadius: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            transition: 'all 0.2s'
          }} 
          onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'; e.currentTarget.style.color = '#6941c6'}} 
          onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4b5563'}}
        >
          <span style={{ fontSize: '20px' }}>{getNodeEmoji(config.label)}</span> 
          <div>
            <div style={{ fontWeight: '500' }}>{config.label}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {getNodeDescription(config.label)}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

            
  
  <div ref={flowWrapperRef} style={{ 
  height: 'calc(100vh - 0px)',  // Changed from calc(100vh - 60px) to ensure full height
  width: '100%', 
  position: 'relative', 
  paddingTop: '60px',           // Add padding-top instead of margin-top to account for fixed header
  boxSizing: 'border-box'       // Ensure padding is included in the element's total size
}}><FlowWrapper>
<ReactFlow
  nodes={nodes}
  edges={edges}
  onNodesChange={onNodesChange}
  onEdgesChange={(changes) => setEdges((eds) => applyEdgeChanges(changes, eds))}
  onConnect={onConnect}
  nodeTypes={nodeTypes}
  fitView
  fitViewOptions={{ padding: 0.1, duration: 0 }}
  style={{ width: '100%', height: '100%' }}
  proOptions={{ hideAttribution: true }}
  defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
  nodesDraggable={!isTesting}
  nodesConnectable={!isTesting}
  elementsSelectable={true}
  selectNodesOnDrag={false}
  minZoom={0.1}
  maxZoom={2.5}
  snapToGrid={true}
  snapGrid={[15, 15]}
  connectionMode="loose"
  onPaneScroll={() => {}} // Prevent excessive resize triggers from panning
>
  <Background
    gap={16} 
    size={1}
    variant="dots"
    />
  <Controls 
    style={{
      bottom: '20px',
      right: '20px',
      top: 'auto',
      left: 'auto',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
      border: '1px solid #f0f0f0',
      borderRadius: '8px',
      padding: '4px'
    }}
  />
</ReactFlow>
</FlowWrapper>
</div>
          </div>
        );

        case 'api':
  return (
    <div style={{ padding: '32px', height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          color: '#1f1f1f', 
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          API Reference
        </h2>
        <p style={{ 
          fontSize: '14px', 
          color: '#6b7280', 
          marginBottom: '32px',
          maxWidth: '700px',
          lineHeight: '1.5'
        }}>
          Integrate your flows with other applications by using our API endpoints. Each flow can be accessed programmatically via HTTP requests.
        </p>
        
        <div style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          padding: '24px', 
          marginBottom: '32px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1f1f1f', 
            marginBottom: '16px'
          }}>
            Flow Execution Endpoint
          </h3>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              alignItems: 'center', 
              background: '#f9fafb', 
              padding: '12px 16px', 
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                padding: '4px 8px', 
                background: '#6941c6', 
                color: '#fff', 
                borderRadius: '4px'
              }}>
                POST
              </span>
              <code style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px', 
                color: '#4b5563',
                flex: 1
              }}>
                https://app.flowbuilder.ai/api/flow/{'{flow_id}'}
              </code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`https://app.flowbuilder.ai/api/flow/{flow_id}`);
                  toast.success('Endpoint copied to clipboard');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#6941c6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '16px', marginBottom: '20px', lineHeight: '1.5' }}>
              Replace <code style={{ fontFamily: 'monospace', backgroundColor: '#f3f4f6', padding: '2px 4px', borderRadius: '4px' }}>{'{flow_id}'}</code> with the ID of your flow. You can find this ID in your flow settings or by clicking "Copy ID" in the flows table.
            </p>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '12px' }}>Headers</h4>
            <div style={{ 
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #f0f0f0' }}>Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #f0f0f0' }}>Value</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #f0f0f0' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px 16px', color: '#1f1f1f', fontFamily: 'monospace', borderBottom: '1px solid #f0f0f0' }}>Content-Type</td>
                    <td style={{ padding: '12px 16px', color: '#1f1f1f', fontFamily: 'monospace', borderBottom: '1px solid #f0f0f0' }}>application/json</td>
                    <td style={{ padding: '12px 16px', color: '#6b7280', borderBottom: '1px solid #f0f0f0' }}>Request body format</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px 16px', color: '#1f1f1f', fontFamily: 'monospace' }}>Authorization</td>
                    <td style={{ padding: '12px 16px', color: '#1f1f1f', fontFamily: 'monospace' }}>Bearer {'{api_key}'}</td>
                    <td style={{ padding: '12px 16px', color: '#6b7280' }}>Your API key from settings</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '12px' }}>Request Body</h4>
            <div style={{ background: '#1e1e1e', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', color: '#d4d4d4', lineHeight: '1.5' }}>
{`{
  "messages": [
    {
      "role": "user",
      "content": "Your message here"
    }
  ],
  "metadata": {
    "user_id": "optional-user-identifier",
    "session_id": "optional-session-id"
  },
  "variables": {
    "contact.first_name":"John",
    "contact.last_name":"Doe"
  }
}`}
              </pre>
            </div>
          </div>
          
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '12px' }}>Response</h4>
            <div style={{ background: '#1e1e1e', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', color: '#d4d4d4', lineHeight: '1.5' }}>
{`{
  "flow_id": "flow-123456",
  "response": "AI response content based on your flow",
  "metadata": {
    "execution_time": 1.45,
    "completion_tokens": 156,
    "total_tokens": 245
  }
}`}
              </pre>
            </div>
          </div>
        </div>
        
        <div style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          padding: '24px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1f1f1f', 
            marginBottom: '16px'
          }}>
            Code Examples
          </h3>
          
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>cURL</h4>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`curl -X POST \\
  https://app.flowbuilder.ai/api/flow/flow-123456 \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your_api_key" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": "Your message here"
      }
    ]
  }'`);
                  toast.success('cURL command copied to clipboard');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#6941c6',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Copy
              </button>
            </div>
            <div style={{ background: '#1e1e1e', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', color: '#d4d4d4', lineHeight: '1.5' }}>
{`curl -X POST \\
  https://app.flowbuilder.ai/api/flow/flow-123456 \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your_api_key" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": "Your message here"
      }
    ]
  }'`}
              </pre>
            </div>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>JavaScript</h4>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`async function callFlow() {
  const response = await fetch('https://app.flowbuilder.ai/api/flow/flow-123456', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer your_api_key'
    },
    body: JSON.stringify({
      messages: [
        {
          role: 'user',
          content: 'Your message here'
        }
      ]
    })
  });
  
  const data = await response.json();
  console.log(data.response);
}

callFlow();`);
                  toast.success('JavaScript code copied to clipboard');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#6941c6',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Copy
              </button>
            </div>
            <div style={{ background: '#1e1e1e', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', color: '#d4d4d4', lineHeight: '1.5' }}>
{`async function callFlow() {
  const response = await fetch('https://app.flowbuilder.ai/api/flow/flow-123456', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer your_api_key'
    },
    body: JSON.stringify({
      messages: [
        {
          role: 'user',
          content: 'Your message here'
        }
      ]
    })
  });
  
  const data = await response.json();
  console.log(data.response);
}

callFlow();`}
              </pre>
            </div>
          </div>
          
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>Python</h4>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`import requests

url = "https://app.flowbuilder.ai/api/flow/flow-123456"
headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer your_api_key"
}
payload = {
    "messages": [
        {
            "role": "user",
            "content": "Your message here"
        }
    ]
}

response = requests.post(url, headers=headers, json=payload)
data = response.json()
print(data["response"])`);
                  toast.success('Python code copied to clipboard');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#6941c6',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Copy
              </button>
            </div>
            <div style={{ background: '#1e1e1e', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', color: '#d4d4d4', lineHeight: '1.5' }}>
{`import requests

url = "https://app.flowbuilder.ai/api/flow/flow-123456"
headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer your_api_key"
}
payload = {
    "messages": [
        {
            "role": "user",
            "content": "Your message here"
        }
    ]
}

response = requests.post(url, headers=headers, json=payload)
data = response.json()
print(data["response"])`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

// ==================== UPDATED DOCS TAB CONTENT ====================
// This should replace the docs case in your renderTabContent function

case 'docs':
  return (
    <div style={{ padding: '32px', height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          color: '#1f1f1f', 
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 8V4H20V8M4 12H20M4 16H20M4 20H20" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Documentation
        </h2>
        <p style={{ 
          fontSize: '14px', 
          color: '#6b7280', 
          marginBottom: '32px',
          maxWidth: '700px',
          lineHeight: '1.5'
        }}>
          Learn how to use Flow Builder to create and automate intelligent workflows with AI.
        </p>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: '20px',
          marginBottom: '40px'
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s'
          }} onMouseEnter={(e) => {e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'}}>
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '8px', 
                background: '#f0f9ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0369a1'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9H8M16 9H18M12 13H8M12 17H8M16 13H18M16 17H18M18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V5C4 4.46957 4.21071 3.96086 4.58579 3.58579C4.96086 3.21071 5.46957 3 6 3H18C18.5304 3 19.0391 3.21071 19.4142 3.58579C19.7893 3.96086 20 4.46957 20 5V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21Z" stroke="#0369a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>
                Getting Started
              </h3>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <ul style={{ 
                margin: 0, 
                padding: 0, 
                listStyle: 'none'
              }}>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Introduction to Flow Builder
                  </a>
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Creating Your First Flow
                  </a>
                </li>
                <li>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Understanding Nodes
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s'
          }} onMouseEnter={(e) => {e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'}}>
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '8px', 
                background: '#f0fdf4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#16a34a'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H9M15 12H19M9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>
                Flow Building
              </h3>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <ul style={{ 
                margin: 0, 
                padding: 0, 
                listStyle: 'none'
              }}>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Node Types & Capabilities
                  </a>
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Connecting Nodes
                  </a>
                </li>
                <li>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Testing & Debugging
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s'
          }} onMouseEnter={(e) => {e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'}}>
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '8px', 
                background: '#f0f9ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6366f1'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>
                API Integration
              </h3>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <ul style={{ 
                margin: 0, 
                padding: 0, 
                listStyle: 'none'
              }}>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    API Authentication
                  </a>
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Webhooks
                  </a>
                </li>
                <li>
                  <a href="#" style={{ 
                    display: 'block', 
                    color: '#6941c6', 
                    fontSize: '14px', 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }} onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}} onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}>
                    Rate Limits
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          padding: '24px', 
          marginBottom: '32px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1f1f1f', 
            marginBottom: '16px'
          }}>
            Node Types Reference
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px'
          }}>
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>🚀</span>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>Start</h4>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                The entry point for your flow. Every flow must have a Start node.
              </p>
            </div>
            
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>🤖</span>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>Chat Completion</h4>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                Generate AI-powered responses using OpenAI's GPT models.
              </p>
            </div>
            
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>🔀</span>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>If/Else</h4>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                Create conditional branches based on message content.
              </p>
            </div>
            
            <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>⚖️</span>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>AI Decision</h4>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                Let AI classify and route messages based on their content.
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ 
          background: '#fff', 
          borderRadius: '12px', 
          padding: '24px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: '24px'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '12px', 
            background: '#f9f5ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6941c6',
            flexShrink: 0
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 3H21V9" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14L21 3" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '4px' }}>
              Need more help?
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>
              Check out our comprehensive documentation or contact our support team.
            </p>
          </div>
          <div>
            <button style={{ 
              padding: '10px 16px', 
              background: '#6941c6', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
            }}>
              View Full Documentation
            </button>
          </div>
        </div>
      </div>
    </div>
  );


        
        case 'settings':
          return (
            <div style={{ padding: '32px', height: '100%', overflowY: 'auto' }}>
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: '#1f1f1f', 
                  marginBottom: '8px' 
                }}>
                  Workspace Settings
                </h2>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#6b7280', 
                  marginBottom: '32px' 
                }}>
                  Manage your workspace preferences, API credentials, and team access.
                </p>
                
                {/* Workspace Info Section */}
                <div style={{ 
  background: '#fff', 
  borderRadius: '12px', 
  padding: '24px', 
  marginBottom: '32px',
  border: '1px solid #f0f0f0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
}}>
  <h3 style={{ 
    fontSize: '18px', 
    fontWeight: '600', 
    color: '#1f1f1f', 
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 15L16 10L5 21" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Workspace Information
  </h3>
  
  <div style={{ marginBottom: '16px' }}>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      Workspace Name
    </label>
    {editingWorkspaceName ? (
      <div style={{ display: 'flex', gap: '8px' }}>
        <input 
          type="text" 
          value={newWorkspaceName} 
          onChange={(e) => setNewWorkspaceName(e.target.value)} 
          style={{ 
            flex: 1,
            padding: '12px 14px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            boxSizing: 'border-box',
            transition: 'all 0.2s'
          }} 
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
          autoFocus
        />
        <button
          onClick={updateWorkspaceName}
          style={{
            padding: '0 16px',
            background: '#6941c6',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Save
        </button>
        <button
          onClick={() => setEditingWorkspaceName(false)}
          style={{
            padding: '0 16px',
            background: '#f9fafb',
            color: '#4b5563',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Cancel
        </button>
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input 
          type="text" 
          value={selectedWorkspace?.name || ''} 
          readOnly
          style={{ 
            flex: 1,
            padding: '12px 14px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            boxSizing: 'border-box',
            background: '#f9fafb'
          }} 
        />
        <button
          onClick={() => {
            setNewWorkspaceName(selectedWorkspace?.name || '');
            setEditingWorkspaceName(true);
          }}
          style={{
            padding: '0 16px',
            background: '#f9fafb',
            color: '#4b5563',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            height: '44px'
          }}
        >
          Edit
        </button>
      </div>
    )}
  </div>
  
  <div>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      Workspace ID
    </label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input 
        type="text" 
        value={selectedWorkspace?.id || ''} 
        readOnly
        style={{ 
          flex: 1,
          padding: '12px 14px', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px', 
          fontSize: '14px', 
          boxSizing: 'border-box',
          background: '#f9fafb',
          color: '#6b7280',
          fontFamily: 'monospace'
        }} 
      />
      <button 
        onClick={() => {
          navigator.clipboard.writeText(selectedWorkspace?.id || '');
          toast.success('Workspace ID copied to clipboard');
        }}
        style={{ 
          padding: '0 16px', 
          background: '#f9fafb', 
          color: '#4b5563', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px', 
          cursor: 'pointer', 
          fontSize: '14px',
          fontWeight: '500',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="9" y="9" width="13" height="13" rx="2" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Copy ID
      </button>
    </div>
  </div>
</div>
                
                {/* API Credentials Section */}
                <div style={{ 
                  background: '#fff', 
                  borderRadius: '12px', 
                  padding: '24px', 
                  marginBottom: '32px',
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: '#1f1f1f', 
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19.4 15C19.1277 15.6171 19.2583 16.3378 19.73 16.82L19.79 16.88C20.1231 17.2135 20.3087 17.6729 20.3087 18.1525C20.3087 18.6321 20.1231 19.0915 19.79 19.425C19.4565 19.7581 18.9971 19.9437 18.5175 19.9437C18.0379 19.9437 17.5785 19.7581 17.245 19.425L17.185 19.365C16.7028 18.8933 15.9821 18.7627 15.365 19.035C14.7669 19.2897 14.3948 19.8719 14.395 20.5V20.7C14.395 21.6941 13.5891 22.5 12.595 22.5C11.6009 22.5 10.795 21.6941 10.795 20.7V20.615C10.7847 19.9718 10.3903 19.3961 9.77498 19.16C9.15789 18.8877 8.43716 19.0183 7.95498 19.49L7.89498 19.55C7.56147 19.8831 7.10207 20.0687 6.62248 20.0687C6.14288 20.0687 5.68348 19.8831 5.34998 19.55C5.0169 19.2165 4.83127 18.7571 4.83127 18.2775C4.83127 17.7979 5.0169 17.3385 5.34998 17.005L5.40998 16.945C5.88171 16.4628 6.01236 15.7421 5.73998 15.125C5.48526 14.5269 4.90307 14.1548 4.27498 14.155H4.07498C3.08087 14.155 2.27498 13.3491 2.27498 12.355C2.27498 11.3609 3.08087 10.555 4.07498 10.555H4.15998C4.80324 10.5447 5.37886 10.1503 5.61498 9.535C5.88724 8.91791 5.75659 8.19718 5.28498 7.715L5.22498 7.655C4.89189 7.3215 4.70626 6.86209 4.70626 6.3825C4.70626 5.9029 4.89189 5.4435 5.22498 5.11C5.55847 4.77691 6.01787 4.59128 6.49748 4.59128C6.97707 4.59128 7.43647 4.77691 7.76998 5.11L7.82998 5.17C8.31216 5.64173 9.03289 5.77238 9.64998 5.5C10.2481 5.24527 10.6202 4.66309 10.62 4.035V3.835C10.62 2.84089 11.4259 2.035 12.42 2.035C13.4141 2.035 14.22 2.84089 14.22 3.835V3.92C14.2202 4.54809 14.5923 5.13028 15.19 5.385C15.8071 5.65738 16.5278 5.52673 17.01 5.055L17.07 4.995C17.4035 4.66191 17.8629 4.47628 18.3425 4.47628C18.8221 4.47628 19.2815 4.66191 19.615 4.995C19.9481 5.32849 20.1337 5.78789 20.1337 6.2675C20.1337 6.74709 19.9481 7.20649 19.615 7.54L19.555 7.6C19.0833 8.08217 18.9527 8.8029 19.225 9.42C19.4797 10.0181 20.0619 10.3902 20.7 10.39H20.9C21.8941 10.39 22.7 11.1959 22.7 12.19C22.7 13.1841 21.8941 13.99 20.9 13.99H20.815C20.1718 13.9903 19.5961 14.3847 19.36 15" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    API Credentials
                  </h3>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    marginBottom: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ 
                        fontSize: '14px', 
                        color: '#4b5563', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        Credential Name
                      </label>
                      <input 
                        type="text" 
                        placeholder="e.g. OpenAI API Key" 
                        id="credName" 
                        style={{ 
                          width: '100%', 
                          padding: '12px 14px', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px', 
                          fontSize: '14px', 
                          boxSizing: 'border-box',
                          transition: 'all 0.2s'
                        }} 
                        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
                        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ 
                        fontSize: '14px', 
                        color: '#4b5563', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        API Key Value
                      </label>
                      <input 
                        type="password" 
                        placeholder="sk-..." 
                        id="credValue" 
                        style={{ 
                          width: '100%', 
                          padding: '12px 14px', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px', 
                          fontSize: '14px', 
                          boxSizing: 'border-box',
                          transition: 'all 0.2s'
                        }} 
                        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
                        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', minWidth: '120px' }}>
                      <button 
                        onClick={() => addCredential(document.getElementById('credName').value, document.getElementById('credValue').value)} 
                        style={{ 
                          padding: '12px 20px', 
                          height: '48px',
                          background: '#6941c6', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: '8px', 
                          cursor: 'pointer', 
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Add
                      </button>
                    </div>
                  </div>
                  
                  {credentials.length === 0 ? (
                    <div style={{ 
                      padding: '40px 20px', 
                      textAlign: 'center', 
                      background: '#f9fafb', 
                      borderRadius: '8px',
                      border: '1px dashed #e0e0e0',
                      color: '#6b7280'
                    }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 16px' }}>
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="16" r="1" fill="#9ca3af"/>
                      </svg>
                      <p style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>No credentials saved yet</p>
                      <p style={{ fontSize: '13px' }}>Add API credentials to connect your flows to external services</p>
                    </div>
                  ) : (
                    <div style={{ 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '8px', 
                      overflow: 'hidden'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {credentials.map((cred) => (
                            <tr key={cred.id} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                              <td style={{ padding: '14px 16px', color: '#1f1f1f' }}>{cred.name}</td>
                              <td style={{ padding: '14px 16px', color: '#6b7280', fontFamily: 'monospace' }}>••••••••{cred.value.substring(cred.value.length - 5)}</td>
                              <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                  <button 
                                    onClick={() => {
                                      navigator.clipboard.writeText(cred.value);
                                      toast.success('API key copied to clipboard');
                                    }} 
                                    title="Copy API key"
                                    style={{ 
                                      background: '#f9fafb', 
                                      border: '1px solid #e0e0e0', 
                                      color: '#6b7280', 
                                      borderRadius: '6px', 
                                      width: '32px', 
                                      height: '32px', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center', 
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <button 
                                    onClick={() => {
                                      // Implementation to delete credential would go here
                                      toast.success(`Credential '${cred.name}' deleted`);
                                    }} 
                                    title="Delete credential"
                                    style={{ 
                                      background: '#fff1f0', 
                                      border: '1px solid #ffccc7', 
                                      color: '#ff4d4f', 
                                      borderRadius: '6px', 
                                      width: '32px', 
                                      height: '32px', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center', 
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M3 6H5H21" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Tools Management Section */}
<div style={{ 
  background: '#fff', 
  borderRadius: '12px', 
  padding: '24px', 
  marginBottom: '32px',
  border: '1px solid #f0f0f0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
}}>
  <h3 style={{ 
    fontSize: '18px', 
    fontWeight: '600', 
    color: '#1f1f1f', 
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Function Tools
  </h3>
  {isEditingTool && currentEditingTool && (
  <div style={{ 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    width: '100vw', 
    height: '100vh', 
    background: 'rgba(0, 0, 0, 0.5)', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    zIndex: 1002,
    backdropFilter: 'blur(2px)'
  }}>
    <div style={{ 
      width: '500px', 
      maxHeight: '80vh',
      overflow: 'auto',
      padding: '24px', 
      background: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)', 
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <div style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1f1f1f', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Edit Tool
        </div>
        <button 
          onClick={() => { setIsEditingTool(false); setCurrentEditingTool(null); }} 
          style={{ 
            background: 'none', 
            border: 'none', 
            color: '#9ca3af', 
            cursor: 'pointer', 
            width: '32px', 
            height: '32px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            borderRadius: '6px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
          onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ 
            fontSize: '14px', 
            color: '#4b5563', 
            display: 'block', 
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            Tool Name
          </label>
          <input 
            type="text" 
            id="editToolName" 
            defaultValue={currentEditingTool.name}
            placeholder="E.g., Weather Tool" 
            style={{ 
              width: '100%', 
              padding: '10px 12px', 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px', 
              fontSize: '14px', 
              boxSizing: 'border-box',
              transition: 'all 0.2s'
            }} 
            onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
            onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
          />
        </div>
        
        <div>
          <label style={{ 
            fontSize: '14px', 
            color: '#4b5563', 
            display: 'block', 
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            Tool Function Name
          </label>
          <input 
            type="text" 
            id="editFunctionName" 
            defaultValue={currentEditingTool.function_name}
            placeholder="E.g., get_weather" 
            style={{ 
              width: '100%', 
              padding: '10px 12px', 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px', 
              fontSize: '14px', 
              boxSizing: 'border-box',
              transition: 'all 0.2s'
            }} 
            onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
            onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
          />
        </div>
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          fontSize: '14px', 
          color: '#4b5563', 
          display: 'block', 
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          Tool Description
        </label>
        <input 
          type="text" 
          id="editToolDescription" 
          defaultValue={currentEditingTool.description || ''}
          placeholder="A brief description of what the tool does" 
          style={{ 
            width: '100%', 
            padding: '10px 12px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            boxSizing: 'border-box',
            transition: 'all 0.2s'
          }} 
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
        />
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          fontSize: '14px', 
          color: '#4b5563', 
          display: 'block', 
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          Tool API Instructions
        </label>
        <input 
          type="text" 
          id="editFunctionDescription" 
          defaultValue={currentEditingTool.function_description || ''}
          placeholder="Instructions for the AI on how to use this tool" 
          style={{ 
            width: '100%', 
            padding: '10px 12px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            boxSizing: 'border-box',
            transition: 'all 0.2s'
          }} 
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
        />
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          fontSize: '14px', 
          color: '#4b5563', 
          display: 'block', 
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          API URL
        </label>
        <input 
          type="text" 
          id="editToolUrl" 
          defaultValue={currentEditingTool.url}
          placeholder="https://api.example.com/tools/function" 
          style={{ 
            width: '100%', 
            padding: '10px 12px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            boxSizing: 'border-box',
            transition: 'all 0.2s'
          }} 
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
        />
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          fontSize: '14px', 
          color: '#4b5563', 
          display: 'block', 
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          Parameters Schema (JSON)
          <span style={{ marginLeft: '6px', fontSize: '12px', color: '#9ca3af' }}>Optional</span>
        </label>
        <textarea 
          id="editParametersSchema" 
          rows="6"
          defaultValue={currentEditingTool.function_parameters ? JSON.stringify(currentEditingTool.function_parameters, null, 2) : ''}
          placeholder={`{
  "type": "object",
  "properties": {
    "location": {
      "type": "string",
      "description": "City name or zip code"
    }
  },
  "required": ["location"]
}`}
          style={{ 
            width: '100%', 
            padding: '10px 12px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '13px', 
            boxSizing: 'border-box',
            fontFamily: 'monospace',
            resize: 'vertical',
            transition: 'all 0.2s'
          }} 
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
        />
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <button 
          onClick={() => { setIsEditingTool(false); setCurrentEditingTool(null); }} 
          style={{ 
            padding: '10px 16px', 
            background: '#f9fafb', 
            color: '#4b5563', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            cursor: 'pointer', 
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          Cancel
        </button>
        <button 
          onClick={() => {
            // Get form values
            const toolId = currentEditingTool.id;
            const name = document.getElementById('editToolName').value;
            const functionName = document.getElementById('editFunctionName').value;
            const description = document.getElementById('editToolDescription').value;
            const functionDescription = document.getElementById('editFunctionDescription').value;
            const url = document.getElementById('editToolUrl').value;
            const parametersSchemaStr = document.getElementById('editParametersSchema').value;
            
            let parametersSchema = {};
            try {
              if (parametersSchemaStr.trim()) {
                parametersSchema = JSON.parse(parametersSchemaStr);
              }
            } catch (error) {
              toast.error('Invalid JSON schema for parameters');
              return;
            }
            
            // Call updateTool function
            updateTool(toolId, {
              name,
              function_name: functionName,
              description,
              function_description: functionDescription,
              function_parameters: parametersSchema,
              url
            }).then(success => {
              if (success) {
                setIsEditingTool(false);
                setCurrentEditingTool(null);
              }
            });
          }}
          style={{ 
            padding: '10px 20px', 
            background: '#6941c6', 
            color: '#fff', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="4" strokeDasharray="32" strokeDashoffset="10"></circle>
              </svg>
              Updating...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12L10 17L19 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}
  
  {/* New Tool Form */}
  <div style={{ 
  padding: '20px', 
  background: '#f9fafb', 
  borderRadius: '8px', 
  marginBottom: '24px',
  border: '1px solid #f0f0f0'
}}>
  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '16px' }}>Add New Tool</h4>
  
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
    <div>
      <label style={{ 
        fontSize: '14px', 
        color: '#4b5563', 
        display: 'block', 
        marginBottom: '8px',
        fontWeight: '500'
      }}>
        Tool Name
      </label>
      <input 
        type="text" 
        id="toolName" 
        placeholder="E.g., Weather Tool" 
        style={{ 
          width: '100%', 
          padding: '10px 12px', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px', 
          fontSize: '14px', 
          boxSizing: 'border-box',
          transition: 'all 0.2s'
        }} 
        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
      />
    </div>
    
    <div>
      <label style={{ 
        fontSize: '14px', 
        color: '#4b5563', 
        display: 'block', 
        marginBottom: '8px',
        fontWeight: '500'
      }}>
        Tool Function Name
      </label>
      <input 
        type="text" 
        id="functionName" 
        placeholder="E.g., get_weather" 
        style={{ 
          width: '100%', 
          padding: '10px 12px', 
          border: '1px solid #e0e0e0', 
          borderRadius: '8px', 
          fontSize: '14px', 
          boxSizing: 'border-box',
          transition: 'all 0.2s'
        }} 
        onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
        onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
      />
    </div>
  </div>
  
  <div style={{ marginBottom: '16px' }}>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      Tool Description
    </label>
    <input 
      type="text" 
      id="toolDescription" 
      placeholder="A brief description of what the tool does" 
      style={{ 
        width: '100%', 
        padding: '10px 12px', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        fontSize: '14px', 
        boxSizing: 'border-box',
        transition: 'all 0.2s'
      }} 
      onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
      onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
    />
  </div>
  
  <div style={{ marginBottom: '16px' }}>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      Tool API Instructions
    </label>
    <input 
      type="text" 
      id="functionDescription" 
      placeholder="Instructions for the AI on how to use this tool" 
      style={{ 
        width: '100%', 
        padding: '10px 12px', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        fontSize: '14px', 
        boxSizing: 'border-box',
        transition: 'all 0.2s'
      }} 
      onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
      onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
    />
  </div>
  
  <div style={{ marginBottom: '16px' }}>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      API URL
    </label>
    <input 
      type="text" 
      id="toolUrl" 
      placeholder="https://api.example.com/tools/function" 
      style={{ 
        width: '100%', 
        padding: '10px 12px', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        fontSize: '14px', 
        boxSizing: 'border-box',
        transition: 'all 0.2s'
      }} 
      onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
      onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
    />
  </div>
  
  <div style={{ marginBottom: '20px' }}>
    <label style={{ 
      fontSize: '14px', 
      color: '#4b5563', 
      display: 'block', 
      marginBottom: '8px',
      fontWeight: '500'
    }}>
      Parameters Schema (JSON)
      <span style={{ marginLeft: '6px', fontSize: '12px', color: '#9ca3af' }}>Optional</span>
    </label>
    <textarea 
      id="parametersSchema" 
      rows="6"
      placeholder={`{
  "type": "object",
  "properties": {
    "location": {
      "type": "string",
      "description": "City name or zip code"
    }
  },
  "required": ["location"]
}`}
      style={{ 
        width: '100%', 
        padding: '10px 12px', 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        fontSize: '13px', 
        boxSizing: 'border-box',
        fontFamily: 'monospace',
        resize: 'vertical',
        transition: 'all 0.2s'
      }} 
      onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
      onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
    />
  </div>
    
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <button 
        onClick={() => {
          // Get form values
          const name = document.getElementById('toolName').value;
          const functionName = document.getElementById('functionName').value;
          const description = document.getElementById('toolDescription').value;
          const functionDescription = document.getElementById('functionDescription').value;
          const url = document.getElementById('toolUrl').value;
          const parametersSchema = document.getElementById('parametersSchema').value;
          
          let parameters = {};
          try {
            if (parametersSchema.trim()) {
              parameters = JSON.parse(parametersSchema);
            }
          } catch (error) {
            toast.error('Invalid JSON schema for parameters');
            return;
          }
          
          // Call the addTool function
          addTool({
            name,
            function_name: functionName,
            description,
            function_description: functionDescription,
            function_parameters: parameters,
            url
          }).then(result => {
            if (result) {
              // Clear form on success
              document.getElementById('toolName').value = '';
              document.getElementById('functionName').value = '';
              document.getElementById('toolDescription').value = '';
              document.getElementById('functionDescription').value = '';
              document.getElementById('toolUrl').value = '';
              document.getElementById('parametersSchema').value = '';
            }
          });
        }}
        style={{ 
          padding: '10px 20px', 
          background: '#6941c6', 
          color: '#fff', 
          border: 'none', 
          borderRadius: '8px', 
          cursor: 'pointer', 
          fontSize: '14px', 
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
        }}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="4" strokeDasharray="32" strokeDashoffset="10"></circle>
            </svg>
            Adding...
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Add Tool
          </>
        )}
      </button>
    </div>
  </div>
  
  {/* Tools Table */}
  {tools.length === 0 ? (
    <div style={{ 
      padding: '40px 20px', 
      textAlign: 'center', 
      background: '#f9fafb', 
      borderRadius: '8px',
      border: '1px dashed #e0e0e0',
      color: '#6b7280'
    }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 16px' }}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <p style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>No tools created yet</p>
      <p style={{ fontSize: '13px' }}>Create your first function tool to use in your chat flows</p>
    </div>
  ) : (
    <div style={{ 
      border: '1px solid #f0f0f0', 
      borderRadius: '8px', 
      overflow: 'hidden' 
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
  <thead>
    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tool Name</th>
      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Function Name</th>
      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>URL</th>
      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
    </tr>
  </thead>
        <tbody>
          {tools.map((tool) => (
            <tr key={tool.id} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
              <td style={{ padding: '14px 16px', color: '#1f1f1f' }}>
                <div style={{ fontWeight: '500' }}>{tool.name}</div>
                {tool.description && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{tool.description}</div>
                )}
              </td>
              <td style={{ padding: '14px 16px', color: '#4b5563', fontFamily: 'monospace' }}>{tool.function_name}</td>
              <td style={{ padding: '14px 16px', color: '#4b5563' }}>
                <div style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tool.url}
                </div>
              </td>
              <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(tool.id);
                      toast.success('Tool ID copied to clipboard');
                    }} 
                    title="Copy ID"
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e0e0e0', 
                      color: '#6b7280', 
                      borderRadius: '6px', 
                      width: '32px', 
                      height: '32px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button 
  onClick={() => {
    setCurrentEditingTool(tool);
    setIsEditingTool(true);
  }} 
  title="Edit tool"
  style={{ 
    background: '#f0f9ff', 
    border: '1px solid #bae6fd', 
    color: '#0ea5e9', 
    borderRadius: '6px', 
    width: '32px', 
    height: '32px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    cursor: 'pointer',
    transition: 'all 0.2s'
  }}
>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
</button>
                  <button 
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete the tool "${tool.name}"?`)) {
                        deleteTool(tool.id);
                      }
                    }} 
                    title="Delete tool"
                    style={{ 
                      background: '#fff1f0', 
                      border: '1px solid #ffccc7', 
                      color: '#ff4d4f', 
                      borderRadius: '6px', 
                      width: '32px', 
                      height: '32px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>
                
                {/* Team Access Section */}
                <div style={{ 
                  background: '#fff', 
                  borderRadius: '12px', 
                  padding: '24px',
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: '#1f1f1f',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: 0
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Team Access
                    </h3>
                    <div>
                      <button 
                        style={{ 
                          padding: '8px 12px', 
                          background: '#6941c6', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: '8px', 
                          cursor: 'pointer', 
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Invite User
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    padding: '40px 20px', 
                    textAlign: 'center', 
                    background: '#f9fafb', 
                    borderRadius: '8px',
                    border: '1px dashed #e0e0e0',
                    color: '#6b7280'
                  }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 16px' }}>
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>No team members yet</p>
                    <p style={{ fontSize: '13px' }}>Invite team members to collaborate on your flows</p>
                  </div>
                </div>
              </div>
            </div>
          );
      default:
        return null;
    }
  };

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const closeProfileMenu = () => setIsProfileMenuOpen(false);


  const getNodeDescription = (label) => {
    switch (label) {
      case 'Start': return 'Starting point of your flow';
      case 'Chat Completion': return 'Generate AI responses using LLMs';
      case 'Find and Replace': return 'Search and replace text in messages';
      case 'Knowledge Base': return 'Add context from Pinecone vector database';
      case 'If/Else': return 'Create conditional branches';
      case 'AI Decision': return 'Let AI classify and route messages';
      case 'Output': return 'Final response to the user';
      case 'API Request': return 'Make HTTP requests to external APIs';
      case 'Store Variable': return 'Store values for use elsewhere in the flow';
      default: return '';
    }
  };

  const getNodeEmoji = (label) => {
    switch (label) {
      case 'Start': return '🚀';
      case 'Chat Completion': return '🤖';
      case 'Find and Replace': return '🔁';
      case 'Knowledge Base': return '📚';
      case 'If/Else': return '🔀';
      case 'AI Decision': return '⚖️';
      case 'API Request': return '🔌'; // New node
      case 'Store Variable': return '💾'; // New node
      case 'Output': return '📤';
      default: return '';
    }
  };

  if (isLoading) {
  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      background: '#f9fafb',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      <div style={{ 
        position: 'relative', 
        width: '60px', 
        height: '60px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: '3px solid transparent',
          borderTopColor: '#6941c6',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ 
          position: 'absolute',
          top: '5px',
          left: '5px',
          right: '5px',
          bottom: '5px',
          borderRadius: '50%',
          border: '3px solid transparent',
          borderTopColor: '#b692f6',
          animation: 'spin 0.75s linear infinite reverse'
        }} />
        <div style={{ 
          position: 'absolute',
          top: '10px',
          left: '10px',
          right: '10px',
          bottom: '10px',
          borderRadius: '50%',
          border: '3px solid transparent',
          borderTopColor: '#e9d7fe',
          animation: 'spin 0.5s linear infinite'
        }} />
      </div>
      <div style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        color: '#1f1f1f',
        marginBottom: '8px'
      }}>
        Loading...
      </div>
      <div style={{ fontSize: '14px', color: '#6b7280' }}>
        Preparing your workspace
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

if (!user) {
  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      background: '#f9fafb',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{ 
        width: '420px', 
        padding: '0', 
        background: '#fff', 
        borderRadius: '16px', 
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)', 
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '32px 40px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <img src="/icon.png" alt="Flowbuilder.ai" style={{ width: '56px', height: '56px', marginBottom: '16px' }} />
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#1f1f1f', 
            margin: '0 0 8px 0'
          }}>
            {isSignUp ? 'Create an account' : 'Welcome back'}
          </h2>
          <p style={{ 
            fontSize: '14px', 
            color: '#6b7280', 
            margin: 0,
            lineHeight: '1.5'
          }}>
            {isSignUp ? 'Start building intelligent flows' : 'Sign in to continue to your workspace'}
          </p>
        </div>
        
        <div style={{ padding: '32px 40px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              fontSize: '14px', 
              color: '#4b5563', 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              Email
            </label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="name@example.com" 
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '15px',
                boxSizing: 'border-box',
                transition: 'all 0.2s'
              }} 
              onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
              onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
            />
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ 
                fontSize: '14px', 
                color: '#4b5563', 
                fontWeight: '500'
              }}>
                Password
              </label>
              {!isSignUp && (
                <a href="#" style={{ 
                  fontSize: '14px', 
                  color: '#6941c6', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}>
                  Forgot password?
                </a>
              )}
            </div>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              placeholder="••••••••" 
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '15px',
                boxSizing: 'border-box',
                transition: 'all 0.2s'
              }} 
              onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
              onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
            />
          </div>
          
          <button 
            onClick={isSignUp ? handleSignUp : handleSignIn} 
            style={{ 
              width: '100%', 
              padding: '12px', 
              background: '#6941c6', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '15px', 
              fontWeight: '500',
              marginBottom: '24px',
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
            }}
          >
            {isSignUp ? 'Create Account' : 'Sign In'}
          </button>
          
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={() => setIsSignUp(!isSignUp)} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#6941c6', 
                cursor: 'pointer', 
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

if (!selectedWorkspace && workspaces.length === 0) {
  return (
    <div style={{ 
      padding: '40px', 
      background: '#f9fafb', 
      height: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{ 
        width: '480px', 
        padding: '0', 
        background: '#fff', 
        borderRadius: '16px', 
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)', 
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '32px 40px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <img src="/icon.png" alt="Flowbuilder.ai" style={{ width: '56px', height: '56px', marginBottom: '16px' }} />
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#1f1f1f', 
            margin: '0 0 8px 0'
          }}>
            Create Your First Workspace
          </h2>
          <p style={{ 
            fontSize: '14px', 
            color: '#6b7280', 
            margin: 0,
            lineHeight: '1.5'
          }}>
            Workspaces help you organize your flows and collaborate with your team
          </p>
        </div>
        
        <div style={{ padding: '32px 40px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              fontSize: '14px', 
              color: '#4b5563', 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              Workspace Name
            </label>
            <input 
              type="text" 
              value={flowName} 
              onChange={(e) => setFlowName(e.target.value)} 
              placeholder="My Workspace" 
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0', 
                fontSize: '15px',
                boxSizing: 'border-box',
                transition: 'all 0.2s'
              }} 
              onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
              onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
            />
          </div>
          
          <button 
            onClick={() => createWorkspace(flowName)} 
            style={{ 
              width: '100%', 
              padding: '12px', 
              background: '#6941c6', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '15px', 
              fontWeight: '500',
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
            }}
          >
            Create Workspace
          </button>
        </div>
      </div>
    </div>
  );
}

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative', overflow: 'hidden', background: '#f9fafb', boxSizing: 'border-box', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
  <Toaster 
    position="top-right" 
    toastOptions={{
      style: {
        borderRadius: '8px',
        backgroundColor: '#fff',
        padding: '12px 16px',
        color: '#1f1f1f',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        fontSize: '14px',
      },
      success: {
        iconTheme: {
          primary: '#52c41a',
          secondary: '#fff',
        },
      },
      error: {
        iconTheme: {
          primary: '#ff4d4f',
          secondary: '#fff',
        },
      },
    }}
  />
  <div style={{ display: 'flex', height: '100%' }}>
    {/* Sidebar navigation */}
    <div style={{ 
  width: '240px', 
  background: '#fff', 
  boxShadow: '0 0 15px rgba(0, 0, 0, 0.03)', 
  display: 'flex', 
  flexDirection: 'column', 
  justifyContent: 'space-between',
  zIndex: 100,
  position: 'relative'
}}>
  <div>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      padding: '20px 16px'
    }}>
      <img src="/icon.png" alt="Flowbuilder.ai" style={{ width: '32px', height: '32px' }} />
      <span style={{ 
        marginLeft: '12px', 
        fontSize: '18px', 
        fontWeight: '600', 
        color: '#1f1f1f',
        letterSpacing: '-0.01em'
      }}>Flowbuilder.ai</span>
    </div>
    <div style={{ padding: '0 16px 16px' }}>
      {/* Workspace Selector */}
      <select 
        value={selectedWorkspace?.id || ''} 
        onChange={(e) => { 
          const ws = workspaces.find((w) => w.id === e.target.value); 
          setSelectedWorkspace(ws); 
          fetchFlows(ws.id); 
          setNodes([]); 
          setEdges([]); 
          setNodeInputs({}); 
          setSelectedFlowId(null); 
          window.history.pushState({}, '', '/'); 
        }} 
        style={{ 
          width: '100%', 
          padding: '10px 12px', 
          borderRadius: '8px', 
          border: '1px solid #e0e0e0', 
          fontSize: '14px', 
          background: '#fff', 
          cursor: 'pointer', 
          color: '#1f1f1f',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg' fill='%23666666'%3E%3Cpath d='M0 0h10L5 6z'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          paddingRight: '30px'
        }}
      >
        {workspaces.map((ws) => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
      </select>
      <button 
        onClick={createFlow} 
        style={{ 
          marginTop: '12px', 
          width: '100%', 
          padding: '10px', 
          background: '#6941c6', 
          color: '#fff', 
          border: 'none', 
          borderRadius: '8px', 
          cursor: 'pointer', 
          fontSize: '14px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '8px',
          fontWeight: '500',
          transition: 'all 0.2s',
          boxShadow: '0 1px 2px rgba(105, 65, 198, 0.2)'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5V19M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg> 
        New Flow
      </button>
    </div>
        <nav style={{ marginTop: '8px' }}>
  <div 
    onClick={() => { setTab('home'); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
    style={{ 
      padding: '12px 16px', 
      margin: '0 8px',
      fontSize: '14px', 
      color: tab === 'home' ? '#6941c6' : '#4b5563', 
      background: tab === 'home' ? '#f9f5ff' : 'transparent', 
      borderRadius: '8px', 
      cursor: 'pointer', 
      marginBottom: '4px', 
      transition: 'all 0.2s', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      fontWeight: tab === 'home' ? '500' : 'normal'
    }} 
    onMouseEnter={(e) => (e.currentTarget.style.background = tab === 'home' ? '#f9f5ff' : '#f9fafb')} 
    onMouseLeave={(e) => (e.currentTarget.style.background = tab === 'home' ? '#f9f5ff' : 'transparent')}
  >
    {/* Home Icon - House */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 9.5L12 2L21 9.5V20C21 20.2652 20.8946 20.5196 20.7071 20.7071C20.5196 20.8946 20.2652 21 20 21H4C3.73478 21 3.48043 20.8946 3.29289 20.7071C3.10536 20.5196 3 20.2652 3 20V9.5Z" stroke={tab === 'home' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 21V12H15V21" stroke={tab === 'home' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Home
  </div>
  
  <div 
    onClick={() => { setTab('flows'); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
    style={{ 
      padding: '12px 16px', 
      margin: '0 8px',
      fontSize: '14px', 
      color: tab === 'flows' ? '#6941c6' : '#4b5563', 
      background: tab === 'flows' ? '#f9f5ff' : 'transparent', 
      borderRadius: '8px', 
      cursor: 'pointer', 
      marginBottom: '4px', 
      transition: 'all 0.2s', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      fontWeight: tab === 'flows' ? '500' : 'normal'
    }} 
    onMouseEnter={(e) => (e.currentTarget.style.background = tab === 'flows' ? '#f9f5ff' : '#f9fafb')} 
    onMouseLeave={(e) => (e.currentTarget.style.background = tab === 'flows' ? '#f9f5ff' : 'transparent')}
  >
    {/* Flows Icon - Flow Chart */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="7" height="7" rx="1" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="14" y="3" width="7" height="7" rx="1" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="14" y="14" width="7" height="7" rx="1" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="3" y="14" width="7" height="7" rx="1" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 6.5H14" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 17.5H14" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6.5 10V14" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17.5 10V14" stroke={tab === 'flows' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Flows
  </div>
  
  <div 
    onClick={() => { setTab('api'); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
    style={{ 
      padding: '12px 16px', 
      margin: '0 8px',
      fontSize: '14px', 
      color: tab === 'api' ? '#6941c6' : '#4b5563', 
      background: tab === 'api' ? '#f9f5ff' : 'transparent', 
      borderRadius: '8px', 
      cursor: 'pointer', 
      marginBottom: '4px', 
      transition: 'all 0.2s', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      fontWeight: tab === 'api' ? '500' : 'normal'
    }} 
    onMouseEnter={(e) => (e.currentTarget.style.background = tab === 'api' ? '#f9f5ff' : '#f9fafb')} 
    onMouseLeave={(e) => (e.currentTarget.style.background = tab === 'api' ? '#f9f5ff' : 'transparent')}
  >
    {/* API Icon - Code Brackets */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 18L22 12L16 6" stroke={tab === 'api' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 6L2 12L8 18" stroke={tab === 'api' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    API
  </div>
  
  <div 
    onClick={() => { setTab('docs'); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
    style={{ 
      padding: '12px 16px', 
      margin: '0 8px',
      fontSize: '14px', 
      color: tab === 'docs' ? '#6941c6' : '#4b5563', 
      background: tab === 'docs' ? '#f9f5ff' : 'transparent', 
      borderRadius: '8px', 
      cursor: 'pointer', 
      marginBottom: '4px', 
      transition: 'all 0.2s', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      fontWeight: tab === 'docs' ? '500' : 'normal'
    }} 
    onMouseEnter={(e) => (e.currentTarget.style.background = tab === 'docs' ? '#f9f5ff' : '#f9fafb')} 
    onMouseLeave={(e) => (e.currentTarget.style.background = tab === 'docs' ? '#f9f5ff' : 'transparent')}
  >
    {/* Docs Icon - Document */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke={tab === 'docs' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2V8H20" stroke={tab === 'docs' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 13H8" stroke={tab === 'docs' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 17H8" stroke={tab === 'docs' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 9H9H8" stroke={tab === 'docs' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Docs
  </div>
  
  <div 
    onClick={() => { setTab('settings'); setSelectedFlowId(null); window.history.pushState({}, '', '/'); }} 
    style={{ 
      padding: '12px 16px', 
      margin: '0 8px',
      fontSize: '14px', 
      color: tab === 'settings' ? '#6941c6' : '#4b5563', 
      background: tab === 'settings' ? '#f9f5ff' : 'transparent', 
      borderRadius: '8px', 
      cursor: 'pointer', 
      marginBottom: '4px', 
      transition: 'all 0.2s', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      fontWeight: tab === 'settings' ? '500' : 'normal'
    }} 
    onMouseEnter={(e) => (e.currentTarget.style.background = tab === 'settings' ? '#f9f5ff' : '#f9fafb')} 
    onMouseLeave={(e) => (e.currentTarget.style.background = tab === 'settings' ? '#f9f5ff' : 'transparent')}
  >
    {/* Settings Icon - Cog */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={tab === 'settings' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke={tab === 'settings' ? '#6941c6' : '#4b5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    Settings
  </div>
</nav>
      </div>
      <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }}>
  <div style={{ position: 'relative' }}>
    <button 
      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} 
      style={{ 
        width: '100%',
        padding: '8px 12px', 
        background: 'transparent',
        border: '1px solid #f0f0f0',
        borderRadius: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
      onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ 
          width: '32px', 
          height: '32px', 
          borderRadius: '50%', 
          background: '#e9d7fe', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          color: '#6941c6',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f1f1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '130px' }}>
            {user?.email ? user.email.split('@')[0] : 'User'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {selectedWorkspace?.name || 'Workspace'}
          </div>
        </div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9L12 15L18 9" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
    
    {isProfileMenuOpen && (
      <>
        {/* Overlay to capture clicks outside menu */}
        <div 
          onClick={closeProfileMenu}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 998
          }}
        />

        {/* Usage Meter */}
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ 
        background: '#f9fafb', 
        borderRadius: '8px', 
        padding: '12px', 
        border: '1px solid #f0f0f0' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563' }}>USAGE THIS MONTH</span>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f1f1f' }}>2,450 / 10,000</span>
        </div>
        <div style={{ height: '6px', backgroundColor: '#e0e0e0', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '24.5%', backgroundColor: '#6941c6', borderRadius: '3px' }}></div>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>Plan renews in 12 days</div>
      </div>
    </div>
        
        {/* Profile Menu */}
        <div style={{ 
          position: 'absolute', 
          bottom: '50px', 
          left: '0', 
          width: '208px', 
          background: '#fff', 
          borderRadius: '8px', 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', 
          zIndex: 999,
          border: '1px solid #f0f0f0',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f1f1f', marginBottom: '4px' }}>
              {user?.email || 'user@example.com'}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Free Account
            </div>
          </div>
          <div style={{ padding: '8px' }}>
            <a 
              href="#" 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                padding: '8px 12px', 
                color: '#4b5563', 
                textDecoration: 'none', 
                borderRadius: '6px', 
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
              onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Account Settings
            </a>
            <a 
              href="#" 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                padding: '8px 12px', 
                color: '#4b5563', 
                textDecoration: 'none', 
                borderRadius: '6px', 
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
              onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Help & Support
            </a>
            <div style={{ padding: '8px 0', margin: '8px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ padding: '0 12px', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>
                APP VERSION
              </div>
              <div style={{ padding: '0 12px', fontSize: '13px', color: '#4b5563' }}>
                1.0.0 (Build 2025.02.25)
              </div>
            </div>
            <button 
              onClick={handleSignOut} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                padding: '8px 12px', 
                color: '#ef4444', 
                background: 'transparent',
                border: 'none',
                borderRadius: '6px', 
                fontSize: '14px',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {e.currentTarget.style.background = '#fff5f5'}}
              onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17L21 12L16 7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      </>
    )}
  </div>
</div>
    </div>
    
    {/* Main content area */}
    <div style={{ flex: 1, padding: 0, overflow: 'hidden', position: 'relative' }}>
      {renderTabContent()}
    </div>
  </div>

  {/* Testing Chat Drawer */}
  {isChatDrawerOpen && (
    <div style={{ 
      position: 'fixed', 
      bottom: '0', 
      right: '0', 
      width: '380px', 
      height: '600px', 
      background: '#fff', 
      borderLeft: '1px solid #f0f0f0',
      borderTop: '1px solid #f0f0f0',
      borderTopLeftRadius: '12px',
      boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)', 
      zIndex: '1001', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden'
    }}>
      <div style={{ 
        padding: '16px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f' }}>
          {flowName || 'Unnamed Flow'}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={handleResetChat} 
            style={{ 
              padding: '6px 12px', 
              background: '#f9fafb', 
              color: '#4b5563', 
              border: '1px solid #e0e0e0', 
              borderRadius: '6px', 
              cursor: 'pointer', 
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 4V10H7" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 15C4.15839 17.0732 5.38734 18.8952 7.01166 20.2066C8.63598 21.518 10.6004 22.2642 12.6337 22.3462C14.667 22.4283 16.6799 21.8413 18.4008 20.6645C20.1217 19.4878 21.4577 17.7753 22.2149 15.7928C22.9722 13.8102 23.1144 11.6422 22.624 9.56981C22.1336 7.49744 21.0323 5.61333 19.4618 4.15602C17.8913 2.69872 15.9213 1.73263 13.8089 1.37866C11.6964 1.02469 9.52238 1.29488 7.55 2.15" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reset
          </button>
          <button 
            onClick={() => setIsChatDrawerOpen(false)} 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#9ca3af', 
              width: '32px', 
              height: '32px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: 'pointer', 
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
            onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      <div 
        ref={chatMessagesRef} 
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '16px'
        }}
      >
        {chatMessages.length === 0 ? (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            color: '#9ca3af',
            gap: '12px',
            textAlign: 'center',
            padding: '0 20px'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div style={{ fontSize: '15px', fontWeight: '500' }}>No messages yet</div>
            <div style={{ fontSize: '14px' }}>Type a message below to test your flow</div>
          </div>
        ) : (
          chatMessages.map((msg, index) => (
            <div 
              key={index} 
              style={{ 
                marginBottom: '16px', 
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div style={{
                padding: '12px 16px', 
                background: msg.role === 'user' ? '#e9d7fe' : '#f9fafb', 
                color: msg.role === 'user' ? '#6941c6' : '#1f1f1f', 
                borderRadius: msg.role === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0', 
                maxWidth: '80%',
                fontSize: '14px',
                lineHeight: '1.5',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                {msg.content}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#9ca3af',
                marginTop: '4px',
                padding: '0 2px'
              }}>
                {msg.role === 'user' ? 'You' : 'Assistant'} • {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))
        )}
      </div>
      <div style={{ 
        padding: '16px', 
        borderTop: '1px solid #f0f0f0',
        display: 'flex', 
        gap: '8px' 
      }}>
        <input 
          type="text" 
          value={userInput} 
          onChange={(e) => setUserInput(e.target.value)} 
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} 
          placeholder="Type a message..." 
          style={{ 
            flex: 1, 
            padding: '12px 16px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            background: '#fff',
            transition: 'all 0.2s'
          }} 
          disabled={isTesting}
          onFocus={(e) => {e.target.style.borderColor = '#6941c6'; e.target.style.boxShadow = '0 0 0 2px rgba(105, 65, 198, 0.1)'}}
          onBlur={(e) => {e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = 'none'}}
        />
        <button 
          onClick={handleSendMessage} 
          disabled={isTesting} 
          style={{ 
            padding: '12px 16px', 
            background: '#6941c6', 
            color: '#fff', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: isTesting ? 'not-allowed' : 'pointer', 
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.2s',
            opacity: isTesting ? 0.7 : 1
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 2L11 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  )}
</div>
  );
}

export default App;